/* ════════════════════════════════════════════════════════════════════
 *  MDL Studies — QA/QC module (40 CFR 136 App. B Rev 2).
 *
 *  A faithful React port of the design prototype (mdl_study_module). The
 *  markup + CSS reproduce the prototype EXACTLY (project rule: do not change
 *  the design); imperative DOM logic is replaced by React state. Because the
 *  list is the data-heavy surface, it is built for large datasets — filter
 *  and sort are memoized and only the current page slice is rendered to the
 *  DOM (O(pageSize) nodes regardless of total row count).
 *
 *  NOTE (design-fidelity deviation from CLAUDE.md §18.6): this screen
 *  deliberately uses the prototype's own XAF-style toolbar / grid / title
 *  instead of the shared <AppToolbar>/<AppDataGrid>/<FormTitle>, because the
 *  instruction was to preserve this specific design verbatim. Messages follow
 *  BO UI Standard §13 (two tiers): blocking confirms/errors/guards via the shared
 *  useConfirm() dialog (confirmDlg/alertDlg); routine success/info via useNotify().
 *  The list grid and study detail are REAL data from the API (dbo.MDLStudy*);
 *  the New-Study create + e-signature workflow remains a client-side demo that
 *  the write phase (Phase 2) will persist.
 * ════════════════════════════════════════════════════════════════════ */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import './mdlStudies.css';
import {
  RETRIEVE_SPIKES, RETRIEVE_BLANKS,
  type ListRow,
} from './mdlData';
import {
  buildReportHtml, buildRetrieveMatrixStudy, buildRetrieveAnalytePayload, renderMatrixSection,
  renderKpis, renderSpikeTable, renderBlankTable, renderCalcRows, renderDocRows,
  renderSignatures, renderAttachments, fmtSigDate, studyHasData,
  statusTitle, watermarkLabel, ACTION_REQ, replicateKey, baseSysCode,
  type Study, type StudyStatus, type Signature, type ModelType, type RawResultRow, type MdlCompliance, type MatrixRow,
} from './mdlCalc';
import { fetchMdlList, fetchMdlDetail, createMdlStudy, createMdlStudyMulti, updateMdlStudyMulti, transitionMdlStudy, applyMdl, deleteMdlStudy, uploadMdlAttachment, deleteMdlAttachment, downloadMdlAttachment, fetchMdlLookups, fetchMdlQcCategories, fetchMdlWorkflowConfig, fetchMdlQcTypes, fetchMdlReferenceMdls, fetchMdlBlankExceedance, fetchMdlRetrieve, fetchMdlQcBatches, type MdlQueryLookup, type MdlInstrumentOption, type MdlQcTypeOption, type MdlRetrieveRow, type MdlQcBatchOption } from './api';
import { toListRow, toStudy } from './mdlMap';
import { fmtLocalDate } from '../../../utils/datetime';
import { LoaderSymbol } from '../../../components/loading';
import { useAuthStore } from '../../../auth/store';
import { ColFilterButton, type ColumnFilterValue } from '../../../components/ColumnFilterDropdown';
import { FormTitle } from '../../../components/FormTitle';
import { AppToolbar } from '../../../components/AppToolbar';
import { useHotkeys, formatCombo, type HotkeyDef } from '../../../components/hotkeys';
import { AppDataGrid, GridPager, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { WorkflowStepper } from '../../../components/WorkflowStepper';
import { useBanner } from '../../../layouts/banner';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';

type View = 'list' | 'query' | 'retrieve' | 'study';
type Tab = 'spikes' | 'blanks' | 'calc' | 'docs' | 'raw' | 'mdl';

/* Shared-grid columns for the MDL Studies list (BO UI Standard §9 — one AppDataGrid,
   black cells, only the Status column coloured via the shared status pill). */
const MDL_LIST_COLUMNS: AppColumn<ListRow>[] = [
  { field: 'mdlId', header: 'MDL ID' },
  { field: 'matrix', header: 'Matrix' },
  { field: 'test', header: 'Test' },
  { field: 'method', header: 'Method' },
  { field: 'instrument', header: 'Instrument' },
  { field: 'model', header: 'Model Type' },
  { field: 'ver', header: 'Ver' },
  { field: 'finalMdl', header: 'Final MDL' },
  { field: 'spikes', header: '#Spikes', type: 'number' },
  { field: 'blanks', header: '#Blanks', type: 'number' },
  { field: 'days', header: '#Days', type: 'number' },
  { field: 'pair', header: 'Pair', value: (r) => (r.pair ? 'Yes' : 'No') },
  { field: 'studyDate', header: 'Study Date', type: 'date' },
  { field: 'effective', header: 'Effective', type: 'date' },
  { field: 'nextDue', header: 'Next Due', type: 'date' },
  { field: 'qc', header: 'QC Status', value: (r) => r.qc?.label ?? '—', text: (r) => r.qc?.label ?? '' },
  { field: 'active', header: 'Active', value: (r) => r.active.label },
  { field: 'status', header: 'Status', type: 'status' },
  { field: 'createdBy', header: 'Created By' },
  { field: 'esig', header: 'e-Sig', type: 'number', value: (r) => r.esig ?? 1 },
];
type Compliance = 'iso' | 'part11';

/** TEMPORARY (user request 2026-07-06): skip the Part 11 password + MFA re-entry on the
 *  e-signature dialog. Flip back to `true` to require the two-component credentials again. */
const ESIGN_REQUIRE_CREDENTIALS = false;

// Fallback only — the real signer comes from the auth store (see `currentUser` below).
const DEFAULT_USER = { id: '—', name: 'Unknown User', role: 'Analyst' };

/* workflow step labels + icons (per the prototype's stepper) */
const WF_STEPS: Array<{ label: string; icon: string }> = [
  { label: '1. Query', icon: 'ti-search' },
  { label: '2. Load', icon: 'ti-database' },
  { label: '3. Calculate', icon: 'ti-calculator' },
  { label: '4. Save', icon: 'ti-device-floppy' },
  { label: '5. Submit', icon: 'ti-send' },
  { label: '6. Validate', icon: 'ti-shield' },
  { label: '7. Approve', icon: 'ti-shield-check' },
  { label: '8. Release', icon: 'ti-rocket' },
];

/* ════════ CheckedComboBox (faithful .ccb port) ════════ */
function CheckedCombo({
  options, groups, selected, onChange, placeholder, disabled, searchable, search, onSearch, joinAll,
}: {
  options?: string[];
  groups?: Array<{ label: string; options: string[] }>;
  selected: Set<string>; onChange: (s: Set<string>) => void;
  placeholder: string; disabled?: boolean; searchable?: boolean;
  search?: string; onSearch?: (v: string) => void;
  /** When true, always list the selected values comma-separated (no "All (n)" / "+n more" collapse). */
  joinAll?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const flat = options ?? (groups ? groups.flatMap((g) => g.options) : []);
  const total = flat.length;
  const checked = flat.filter((o) => selected.has(o));
  const valText = checked.length === 0 ? 'None selected'
    : joinAll ? checked.map((c) => c.split(' — ')[0]).join(', ')
      : checked.length === total ? 'All (' + total + ')'
        : checked.length <= 3 ? checked.map((c) => c.split(' — ')[0]).join(', ')
          : checked.slice(0, 3).map((c) => c.split(' — ')[0]).join(', ') + ' (+' + (checked.length - 3) + ' more)';
  const isPlaceholder = checked.length === 0 && disabled;
  const matches = (o: string) => o.toLowerCase().includes((searchable ? q : '').toLowerCase());

  const toggle = (o: string) => { const n = new Set(selected); if (n.has(o)) n.delete(o); else n.add(o); onChange(n); };
  const setAll = (val: boolean) => onChange(val ? new Set(flat) : new Set());

  const renderOpt = (o: string) => (
    <label className="ccb-dd-opt" key={o} style={searchable && !matches(o) ? { display: 'none' } : undefined}>
      <input type="checkbox" checked={selected.has(o)} onChange={() => toggle(o)} />
      <span>{o}</span>
    </label>
  );

  return (
    <div className={'ccb' + (open ? ' open' : '')} ref={ref}>
      <div className={'ccb-trigger' + (disabled ? ' disabled' : '')} onClick={() => !disabled && setOpen((v) => !v)}>
        <span className={'ccb-val' + (isPlaceholder ? ' placeholder' : '')}>{disabled ? placeholder : valText}</span>
        <i className="ti ti-chevron-down ccb-chev" aria-hidden="true" />
      </div>
      <div className="ccb-dropdown">
        {searchable && (
          <div className="ccb-dd-search">
            <input type="text" placeholder="Search parameters..." value={onSearch ? (search ?? '') : q}
              onChange={(e) => { if (onSearch) onSearch(e.target.value); else setQ(e.target.value); }} />
          </div>
        )}
        <div className="ccb-dd-actions">
          <a onClick={() => setAll(true)}>Select all</a>
          <a onClick={() => setAll(false)}>Clear all</a>
          <span className="ccb-dd-count">{checked.length} / {total}</span>
        </div>
        <div className="ccb-dd-opts">
          {options && options.map(renderOpt)}
          {groups && groups.map((g) => (
            <div key={g.label}>
              <div className="ccb-dd-grp">{g.label}</div>
              {g.options.map(renderOpt)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Static HTML helper for dangerouslySetInnerHTML */
const H = (html: string) => ({ __html: html });

/* ── date helpers for the rolling Verification window + auto-pooling (gap 4.3) ── */
const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const isoMinusMonths = (iso: string, m: number) => { const d = new Date(iso + 'T00:00:00'); d.setMonth(d.getMonth() - m); return isoLocal(d); };
const usDate = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso || '') ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : iso);
/** Distinct spiked replicate samples in a retrieve result (by QC type + sys code + batch). */
const countSpiked = (rows: MdlRetrieveRow[]) => new Set(rows.filter((r) => r.kind === 'S').map((r) => `${r.qcTypeName ?? ''}|${baseSysCode(r.sysSampleCode)}|${r.qcBatchId ?? ''}`)).size;

/** Date field that always displays/accepts MM/DD/YYYY, independent of the browser
 *  locale (a native <input type="date"> renders in the OS locale). value & onChange
 *  use ISO yyyy-mm-dd; the text shown and typed is MM/DD/YYYY. A calendar button
 *  opens the browser's native date picker (its calendar grid is locale-neutral);
 *  the typed text overlay keeps the display in MM/DD/YYYY regardless of the pick. */
function DateField({ value, onChange }: { value: string; onChange: (iso: string) => void }) {
  const isoToUs = (iso: string) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || ''); return m ? `${m[2]}/${m[3]}/${m[1]}` : ''; };
  const [text, setText] = useState(isoToUs(value));
  useEffect(() => { setText(isoToUs(value)); }, [value]);
  const pickerRef = useRef<HTMLInputElement>(null);
  const commit = (t: string) => {
    const s = t.trim();
    if (s === '') { onChange(''); return; }
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
    if (!m) return;                                   // incomplete/invalid — keep typing, don't commit
    const mm = m[1]!.padStart(2, '0'), dd = m[2]!.padStart(2, '0'), yyyy = m[3]!;
    if (+mm < 1 || +mm > 12 || +dd < 1 || +dd > 31) return;
    onChange(`${yyyy}-${mm}-${dd}`);
  };
  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') { try { el.showPicker(); return; } catch { /* fall through */ } }
    el.focus(); el.click();   // fallback for browsers without showPicker()
  };
  return (
    <div style={{ position: 'relative', display: 'block' }}>
      <input className="form-control" type="text" inputMode="numeric" placeholder="MM/DD/YYYY" maxLength={10}
        style={{ paddingRight: 30, width: '100%' }}
        value={text} onChange={(e) => { setText(e.target.value); commit(e.target.value); }} onBlur={(e) => commit(e.target.value)} />
      <button type="button" onClick={openPicker} title="Pick a date" aria-label="Pick a date"
        style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: 30, border: 'none', background: 'transparent',
          cursor: 'pointer', color: 'var(--ai-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className="ti ti-calendar" />
      </button>
      {/* Hidden native date input — source of the calendar popup; selection writes ISO yyyy-mm-dd. */}
      <input ref={pickerRef} type="date" value={value || ''} tabIndex={-1} aria-hidden
        onChange={(e) => onChange(e.target.value)}
        style={{ position: 'absolute', right: 4, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
    </div>
  );
}

/* ════════ Raw Results — interactive table with row + column filters (feedback 2026-06-22) ════════ */
const RAW_COLS: { key: keyof RawResultRow; label: string; num?: boolean }[] = [
  { key: 'qcBatchId', label: 'QCBatchID' },        // swapped ahead of SysSampleCode (feedback #4)
  { key: 'sysSampleCode', label: 'SysSampleCode' },
  { key: 'qcType', label: 'QCType' },              // TestParameter.QCType.QCTypeName (verification item 5)
  { key: 'parameter', label: 'Parameter' },
  { key: 'numericResult', label: 'NumericResult', num: true },
  { key: 'result', label: 'Result' },
  { key: 'units', label: 'Units' },
  { key: 'analyzedDate', label: 'AnalyzedDate' },
];
function RawResultsTable({ heading, rows }: { heading: string; rows: RawResultRow[] }) {
  const [q, setQ] = useState('');                                            // Row Filter (search all columns)
  const [colF, setColF] = useState<Record<string, ColumnFilterValue>>({});   // Column Filters (Excel-style popups)
  // QCType column shows only when populated (MDL Verification) — hidden for Initial MDL.
  const cols = useMemo(() => RAW_COLS.filter((c) => c.key !== 'qcType' || rows.some((r) => (r.qcType ?? '') !== '')), [rows]);
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (ql && !cols.some((c) => String(r[c.key] ?? '').toLowerCase().includes(ql))) return false;
      for (const c of cols) {
        const fv = colF[c.key];
        if (fv && !fv.has(String(r[c.key] ?? ''))) return false;            // null = inactive; Set = keep only listed
      }
      return true;
    });
  }, [rows, q, colF, cols]);
  return (
    <div style={{ margin: '0 0 16px' }}>
      <div style={{ fontSize: 12, fontWeight: 'bold', color: 'var(--ai-ink)', padding: '4px 2px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <span>{heading} <span style={{ fontWeight: 'normal', color: 'var(--ai-muted)', fontSize: 11 }}>· {filtered.length} of {rows.length} rows</span></span>
        <input className="form-control" style={{ width: 220, height: 26, fontSize: 12 }} placeholder="Filter rows…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="mtx-wrap">
        <table className="xaf-table" style={{ tableLayout: 'auto' }}>
          <thead>
            <tr>{cols.map((c) => (
              <th key={c.key} className={c.num ? 'num' : undefined}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {c.label}
                  <ColFilterButton variant="inline" col={c.key} label={c.label} rows={rows}
                    formatter={(r) => String(r[c.key] ?? '')} value={colF[c.key] ?? null}
                    onChange={(v) => setColF((p) => ({ ...p, [c.key]: v }))} />
                </span>
              </th>
            ))}</tr>
          </thead>
          <tbody>
            {filtered.length === 0
              ? <tr><td colSpan={cols.length} style={{ color: 'var(--ai-muted)', padding: 10 }}>No matching rows.</td></tr>
              : filtered.map((r, i) => (
                <tr key={i} className={i % 2 ? 'zebra' : undefined}>
                  {cols.map((c) => <td key={c.key} className={c.num ? 'num' : undefined}>{(r[c.key] ?? '') === '' ? '—' : r[c.key]}</td>)}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
/** Compliance panel — 40 CFR 136 App. B checks computed from the retrieved data (gaps 3.1/3.2/3.3). */
function CompliancePanel({ c, modelType }: { c: MdlCompliance; modelType: ModelType }) {
  const Chip = ({ ok, label, detail }: { ok: boolean; label: string; detail: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
      <i className={'ti ' + (ok ? 'ti-circle-check' : 'ti-alert-triangle')} style={{ fontSize: 14, color: ok ? 'var(--ai-green)' : 'var(--ai-red)' }} />
      <span style={{ fontSize: 12 }}><strong>{label}:</strong> {detail}</span>
    </div>
  );
  const lowHigh = c.spikeLevel.filter((s) => s.flag === 'low' || s.flag === 'high');
  const spikeDetail = c.spikeLevelOk
    ? 'all analytes within 2–10× the current MDL'
    : lowHigh.map((s) => `${s.analyte} ${s.ratio != null ? s.ratio.toFixed(1) + '×' : ''} ${s.flag === 'low' ? '(too low)' : '(too high)'}`).join(', ');
  const cadenceDetail = c.cadence.length
    ? c.cadence.map((q) => `${q.quarter}: ${q.spikes}`).join(' · ')
    : 'no quarters in window';
  return (
    <div style={{ background: 'var(--ai-amber-bg)', border: '1px solid var(--ai-amber)', borderRadius: 4, padding: '8px 12px', margin: '4px 0 10px' }}>
      <div style={{ fontSize: 10, fontWeight: 'bold', color: 'var(--ai-amber)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>
        <i className="ti ti-clipboard-check" /> Compliance — 40 CFR 136 App. B
      </div>
      <Chip ok={c.minSpikeOk} label="Spiked replicates ≥ 7" detail={`${c.nSpike} retrieved${c.minSpikeOk ? '' : ' — below minimum'}`} />
      <Chip ok={modelType === 'verif' || c.minBlankOk} label="Method blanks ≥ 7" detail={`${c.nBlank} retrieved${(modelType !== 'verif' && !c.minBlankOk) ? ' — below minimum' : ''}`} />
      <Chip ok={c.spreadOk} label="Spread ≥ 3 batches / ≥ 3 dates" detail={`${c.batches} batch${c.batches === 1 ? '' : 'es'}, ${c.dates} date${c.dates === 1 ? '' : 's'}${c.spreadOk ? '' : ' — insufficient spread'}`} />
      <Chip ok={c.spikeLevelOk} label="Spike level 2–10× MDL" detail={spikeDetail} />
      {modelType === 'verif' && <Chip ok={c.cadenceOk} label="Spike cadence ≥ 2/quarter" detail={cadenceDetail} />}
    </div>
  );
}
/** Outlier exclusion (gap 3.4) — exclude a replicate only with a documented reason; it stays
 *  visible (struck-through in the matrix) but is dropped from the statistics; warns if n drops < 7. */
function OutlierControl({ spikeRows, blankRows, excluded, onChange }: {
  spikeRows: MatrixRow[]; blankRows: MatrixRow[]; excluded: Map<string, string>; onChange: (next: Map<string, string>) => void;
}) {
  const reps = [...spikeRows.map((r) => ({ r, kind: 'Spike' })), ...blankRows.map((r) => ({ r, kind: 'Blank' }))];
  const toggle = (key: string) => {
    if (excluded.has(key)) { const n = new Map(excluded); n.delete(key); onChange(n); return; }
    const reason = window.prompt('Reason for excluding this replicate (required — documented justification per 40 CFR 136 App. B):');
    if (!reason || !reason.trim()) return;
    const n = new Map(excluded); n.set(key, reason.trim()); onChange(n);
  };
  const activeSpikes = spikeRows.filter((r) => !excluded.has(replicateKey(r))).length;
  return (
    <div style={{ border: '1px solid var(--ai-line)', borderRadius: 4, padding: '8px 12px', margin: '10px 0' }}>
      <div style={{ fontSize: 10, fontWeight: 'bold', color: 'var(--ai-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
        <i className="ti ti-flask-off" /> Outlier Exclusion <span style={{ fontWeight: 'normal', textTransform: 'none', color: 'var(--ai-muted)' }}>· exclude a replicate only with documented justification — it stays visible (struck-through) and is removed from the statistics</span>
      </div>
      {excluded.size > 0 && activeSpikes < 7 && (
        <div style={{ color: 'var(--ai-red)', fontSize: 11, marginBottom: 6 }}><i className="ti ti-alert-triangle" /> {activeSpikes} spiked replicate(s) remain after exclusions — below the §136 minimum of 7; add a replacement replicate.</div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {reps.map(({ r, kind }) => {
          const key = replicateKey(r); const ex = excluded.has(key);
          return (
            <button key={kind + key} onClick={() => toggle(key)} title={ex ? 'Excluded: ' + excluded.get(key) + ' — click to restore' : 'Click to exclude as a documented outlier'}
              style={{ fontSize: 11, padding: '2px 8px', borderRadius: 3, cursor: 'pointer', border: '1px solid ' + (ex ? 'var(--ai-red)' : 'var(--ai-line)'), background: ex ? 'var(--ai-pill-alert-bg)' : '#fff', color: ex ? 'var(--ai-red)' : 'var(--ai-ink)', textDecoration: ex ? 'line-through' : 'none' }}>
              {ex ? '⊘ ' : ''}{r.sample || r.id} · {kind}
            </button>
          );
        })}
      </div>
      {excluded.size > 0 && (
        <div style={{ marginTop: 6, fontSize: 10.5, color: 'var(--ai-muted)' }}>
          {[...excluded.entries()].map(([k, reason]) => <div key={k}><strong>{k.split('|')[1] || k}</strong>: {reason}</div>)}
        </div>
      )}
    </div>
  );
}
/** Stage raw-data files before Save (req c) — held client-side, uploaded once the study is created. */
function PreAttach({ files, onChange }: { files: File[]; onChange: (f: File[]) => void }) {
  const add = (fl: FileList | null) => { if (fl) onChange([...files, ...Array.from(fl)]); };
  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-hdr">
        <div className="card-title"><i className="ti ti-paperclip" /> Raw Data &amp; Attachments</div>
        <div className="card-actions">
          <label style={{ cursor: 'pointer', fontSize: 12, color: 'var(--ai-blue)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <i className="ti ti-upload" /> Add Files
            <input type="file" multiple style={{ display: 'none' }} onChange={(e) => { add(e.target.files); e.currentTarget.value = ''; }} />
          </label>
        </div>
      </div>
      <div style={{ padding: 12 }}>
        {files.length === 0
          ? <div style={{ color: 'var(--ai-muted)', fontSize: 12 }}>Attach instrument files (.d, .raw, .csv), PDFs, spreadsheets, logbook scans… they upload when you Save.</div>
          : <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>{files.map((f, i) => (
              <li key={i} style={{ marginBottom: 3 }}>{f.name} <span style={{ color: 'var(--ai-muted)' }}>({Math.round(f.size / 1024)} KB)</span>
                <a style={{ color: 'var(--ai-red)', cursor: 'pointer', marginLeft: 8 }} onClick={() => onChange(files.filter((_, j) => j !== i))}>remove</a></li>))}</ul>}
      </div>
    </div>
  );
}
function RawResults({ spike, blank }: { spike: RawResultRow[]; blank: RawResultRow[] }) {
  return (
    <>
      <RawResultsTable heading="Spike Samples" rows={spike} />
      <RawResultsTable heading="Method Blanks" rows={blank} />
    </>
  );
}

/* ════════ Generic single-select grid-lookup — a .ccb-styled popup containing
 *  a small table; picking a row closes it.  Used for the Test grid (Test +
 *  MethodNumber) and the Instrument grid (InstrumentNumber + InstrumentName). ════════ */
function GridLookup<T>({ rows, value, onPick, rowKey, display, columns, search, placeholder, disabledPlaceholder, disabled, minWidth, popupClassName, searchPlaceholder }: {
  rows: T[]; value: T | null; onPick: (r: T) => void;
  rowKey: (r: T) => string; display: (r: T) => string;
  columns: { header: string; cell: (r: T) => React.ReactNode }[];
  search: (r: T) => string; placeholder: string; disabledPlaceholder?: string; disabled?: boolean; minWidth?: number;
  /** Extra class on the popup — the Query Panel widens it via .qp-lk-pop. */
  popupClassName?: string;
  searchPlaceholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const ql = q.toLowerCase();
  const filtered = rows.filter((r) => !ql || search(r).toLowerCase().includes(ql));
  const valKey = value ? rowKey(value) : null;
  return (
    <div className={'ccb' + (open ? ' open' : '')} ref={ref}>
      <div className={'ccb-trigger' + (disabled ? ' disabled' : '')} onClick={() => !disabled && setOpen((v) => !v)}>
        <span className={'ccb-val' + (value ? '' : ' placeholder')}>{disabled ? (disabledPlaceholder ?? placeholder) : value ? display(value) : placeholder}</span>
        <i className="ti ti-chevron-down ccb-chev" aria-hidden="true" />
      </div>
      <div className={'ccb-dropdown' + (popupClassName ? ' ' + popupClassName : '')} style={{ minWidth: minWidth ?? 340 }}>
        <div className="ccb-dd-search"><input type="text" placeholder={searchPlaceholder ?? 'Search…'} value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <div className="ccb-dd-opts" style={{ maxHeight: 260 }}>
          <table className="xaf-table" style={{ width: '100%' }}>
            <thead><tr>{columns.map((c) => <th key={c.header}>{c.header}</th>)}</tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={columns.length} style={{ textAlign: 'center', color: 'var(--ai-muted)', padding: 12 }}>No matches</td></tr>}
              {filtered.map((r) => (
                <tr key={rowKey(r)} className={valKey === rowKey(r) ? 'selected' : ''}
                  style={{ cursor: 'pointer' }} onClick={() => { onPick(r); setOpen(false); setQ(''); }}>
                  {columns.map((c) => <td key={c.header}>{c.cell(r)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** Query-Panel QC batch picker (Spike Run Pick / Blank Batch Pick).
 *
 *  The runs behind the study are TICKED, not typed, so the dataset the MDL rests on
 *  is a visible, auditable selection. The footer sums the SAMPLES the ticked batches
 *  carry — 40 CFR 136 App. B §2(b) counts replicates, not runs, so 5 runs may be 5
 *  or 15 replicates — and turns red below the minimum while you are still picking,
 *  rather than after Load. Its Load button applies the pick without travelling back
 *  up to the toolbar. */
function BatchPick({ kind, batches, picked, onChange, disabled, loading, min, onLoad }: {
  kind: 'S' | 'B';
  batches: MdlQcBatchOption[];
  picked: Set<string>;
  onChange: (s: Set<string>) => void;
  disabled?: boolean; loading?: boolean; min: number;
  onLoad: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const ql = q.trim().toLowerCase();
  const shown = batches.filter((b) => !ql || `${b.batchId ?? ''} ${b.qcType ?? ''}`.toLowerCase().includes(ql));
  const reps = batches.filter((b) => picked.has(b.batchId ?? '')).reduce((t, b) => t + (b.samples || 0), 0);
  const short = picked.size > 0 && reps < min;
  const label = disabled ? '— Select Test first —'
    : loading ? 'Loading batches…'
    : batches.length === 0 ? 'No QC batches in this date range'
    : picked.size === 0 ? '— none ticked —'
    : picked.size === batches.length ? `All (${batches.length})`
    : `${picked.size} of ${batches.length} selected`;

  const toggle = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next);
  };

  return (
    <div className={'ccb' + (open ? ' open' : '')} ref={ref}>
      <div className={'ccb-trigger' + (disabled ? ' disabled' : '')} onClick={() => !disabled && setOpen((v) => !v)}>
        <span className={'ccb-val' + (picked.size ? '' : ' placeholder')}>{label}</span>
        <i className="ti ti-chevron-down ccb-chev" aria-hidden="true" />
      </div>
      <div className="ccb-dropdown" style={{ minWidth: 330 }}>
        <div className="ccb-dd-search">
          <input type="text" placeholder="Search batch or type…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="ccb-dd-actions">
          <a onClick={() => onChange(new Set(batches.map((b) => b.batchId ?? '')))}>Select all</a>
          <a onClick={() => onChange(new Set())}>Clear all</a>
          <span className="ccb-dd-count">{picked.size} / {batches.length}</span>
        </div>
        <div className="ccb-dd-opts">
          {shown.length === 0 && (
            <div style={{ padding: 10, fontSize: 11.5, color: 'var(--ai-muted)', textAlign: 'center' }}>
              No QC batches in this date range.
            </div>
          )}
          {shown.map((b) => (
            <label className="ccb-dd-opt" key={b.batchId ?? ''}>
              <input type="checkbox" checked={picked.has(b.batchId ?? '')} onChange={() => toggle(b.batchId ?? '')} />
              <span>
                <b style={{ fontFamily: 'var(--ai-font-mono)' }}>{b.batchId}</b>
                {b.qcType ? ` · ${b.qcType}` : ''}
                {b.date ? ` · ${fmtLocalDate(b.date)}` : ''}
                {' · '}
                <span style={{ color: 'var(--ai-muted)' }}>{b.samples} sample{b.samples === 1 ? '' : 's'}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="ccb-dd-foot">
          <span className={'ccb-dd-sum' + (short ? ' short' : '')}>
            <b>{reps}</b> {kind === 'S' ? 'spiked' : 'blank'} sample{reps === 1 ? '' : 's'}
            {short ? ` — ${min} required` : ''}
          </span>
          <span className="ccb-dd-btns">
            <button className="ccb-dd-btn" onClick={() => setOpen(false)}>Close</button>
            <button className="ccb-dd-btn primary" onClick={() => { setOpen(false); onLoad(); }}>
              <i className="ti ti-database-search" /> Load
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

/** Multi-select grid lookup — checkbox per row, columns, comma-separated display.
 *  Used for the MDL Verification "QC Type" picker (value = Set of row keys). */
function MultiGridLookup<T>({ rows, selected, onChange, rowKey, columns, search, placeholder, disabledPlaceholder, disabled, minWidth }: {
  rows: T[]; selected: Set<string>; onChange: (next: Set<string>) => void;
  rowKey: (r: T) => string;
  columns: { header: string; cell: (r: T) => React.ReactNode }[];
  search: (r: T) => string; placeholder: string; disabledPlaceholder: string; disabled?: boolean; minWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const ql = q.toLowerCase();
  const filtered = rows.filter((r) => !ql || search(r).toLowerCase().includes(ql));
  const label = selected.size ? Array.from(selected).join(', ') : '';
  const toggle = (k: string) => { const n = new Set(selected); if (n.has(k)) n.delete(k); else n.add(k); onChange(n); };
  const allShownKeys = filtered.map(rowKey);
  const allChecked = allShownKeys.length > 0 && allShownKeys.every((k) => selected.has(k));
  return (
    <div className={'ccb' + (open ? ' open' : '')} ref={ref}>
      <div className={'ccb-trigger' + (disabled ? ' disabled' : '')} onClick={() => !disabled && setOpen((v) => !v)}>
        <span className={'ccb-val' + (label ? '' : ' placeholder')}>{disabled ? disabledPlaceholder : label || placeholder}</span>
        <i className="ti ti-chevron-down ccb-chev" aria-hidden="true" />
      </div>
      <div className="ccb-dropdown" style={{ minWidth: minWidth ?? 360 }}>
        <div className="ccb-dd-search"><input type="text" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <div className="ccb-dd-opts" style={{ maxHeight: 260 }}>
          <table className="xaf-table" style={{ width: '100%' }}>
            <thead><tr>
              <th style={{ width: 28 }}>
                <input type="checkbox" checked={allChecked}
                  onChange={() => { const n = new Set(selected); if (allChecked) allShownKeys.forEach((k) => n.delete(k)); else allShownKeys.forEach((k) => n.add(k)); onChange(n); }} />
              </th>
              {columns.map((c) => <th key={c.header}>{c.header}</th>)}
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={columns.length + 1} style={{ textAlign: 'center', color: 'var(--ai-muted)', padding: 12 }}>No matches</td></tr>}
              {filtered.map((r) => {
                const k = rowKey(r);
                return (
                  <tr key={k} className={selected.has(k) ? 'selected' : ''} style={{ cursor: 'pointer' }} onClick={() => toggle(k)}>
                    <td style={{ textAlign: 'center' }}><input type="checkbox" readOnly checked={selected.has(k)} /></td>
                    {columns.map((c) => <td key={c.header}>{c.cell(r)}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function MdlStudiesPage(_props: { onHome?: () => void }) {
  const [view, setView] = useState<View>('list');
  const [compliance, setCompliance] = useState<Compliance>('iso');

  // The signer is the REAL logged-in user (auth store), not a hardcoded demo user.
  // User ID = login email (falls back to name); role = Administrator when isAdmin,
  // else the first granted role. The authoritative SignedByName is the Employee
  // Display Name resolved server-side at write time.
  const authUser = useAuthStore((s) => s.user);
  const authPerms = useAuthStore((s) => s.permissions);
  // Admin = the server isAdmin flag OR an admin-named role (e.g. "Administrator").
  // Admins have authority for every workflow action (Validate/Approve/Release).
  const isAdminUser = !!authPerms?.isAdmin || /admin/i.test(authPerms?.roles ?? '');
  const currentUser = useMemo(() => ({
    id: authUser?.name || authUser?.email || authUser?.id || DEFAULT_USER.id,   // show the login NAME (e.g. "Service")
    name: authUser?.name || DEFAULT_USER.name,
    role: isAdminUser ? 'Administrator' : (authPerms?.roles?.split(/[,;]/)[0]?.trim() || DEFAULT_USER.role),
  }), [authUser, authPerms, isAdminUser]);

  /* ── List state ── real studies from the API + any locally-created
   *  (not-yet-persisted) studies from the create flow (Phase 2 persists). ── */
  const listQ = useQuery({ queryKey: ['mdl-studies'], queryFn: ({ signal }) => fetchMdlList(signal) });
  const rows = useMemo<ListRow[]>(() => (listQ.data ?? []).map(toListRow), [listQ.data]);
  const listMs = useFetchMs(listQ.isFetching);
  // Dashboard KPIs computed from real data — Due / Overdue derive from NextDue = Effective + 13 months (§3 / gap 4.1).
  const kpi = useMemo(() => {
    const d = listQ.data ?? [];
    const today = isoLocal(new Date());
    const in90 = isoLocal((() => { const x = new Date(); x.setDate(x.getDate() + 90); return x; })());
    const isDate = (s: string | null): s is string => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);
    return {
      total: d.length,
      active: d.filter((x) => x.active).length,
      due: d.filter((x) => isDate(x.nextDue) && x.nextDue >= today && x.nextDue <= in90).length,
      overdue: d.filter((x) => isDate(x.nextDue) && x.nextDue < today).length,
      pending: d.filter((x) => x.mdlStatus === 1 || x.mdlStatus === 2).length,   // Validated/Approved — awaiting sign-off
    };
  }, [listQ.data]);
  // List sort / filter / pagination are handled by the shared <AppDataGrid> (BO UI Standard §9).
  const [selected, setSelected] = useState<Set<string | number>>(new Set());   // checked grid rows (for Delete)
  const [applyPick, setApplyPick] = useState<{ names: string[]; sel: Set<string> } | null>(null);   // C12 — Apply-MDL parameter picker
  // C5 — reopen-after-rollback: a saved study reloaded into the editable Calculate & Save view.
  type EditFlatRow = { analyte: string; qcBatchId: string; sampleId: string; sysSampleCode: string; qcTypeName: string; result: string; kind: string; value: number | null; spikeAmount: number | null; currentMdl: number | null; units: string | null; analyzedDate: string | null };
  const [editStudy, setEditStudy] = useState<{ oid: string; rows: EditFlatRow[]; meta: { matrix: string; test: string; method: string; instrument: string; modelType: ModelType; dateFrom: string; dateTo: string } } | null>(null);
  // Leaving the Calculate & Save view discards any reopen-for-edit session.
  useEffect(() => { if (view !== 'retrieve') setEditStudy(null); }, [view]);

  /* ── New-study (Type → Query → Retrieve) ── */
  const [typeOpen, setTypeOpen] = useState(false);
  const [selType, setSelType] = useState<ModelType>('initial');
  const [qModel, setQModel] = useState<ModelType>('initial');
  // Study Parameters: DB-driven Matrix → Test(grid) → Method/Instrument (TestMethod ↔ Method ↔ Matrix).
  const lookupsQ = useQuery({ queryKey: ['mdl-lookups'], queryFn: ({ signal }) => fetchMdlLookups(signal) });
  // Workflow level config (dbo.ResultEntryDefaultSettings): skip Validate / Approve levels.
  const wfCfgQ = useQuery({ queryKey: ['mdl-wfconfig'], queryFn: ({ signal }) => fetchMdlWorkflowConfig(signal) });
  const skipValidate = wfCfgQ.data?.skipValidate ?? false;
  const skipApprove = wfCfgQ.data?.skipApprove ?? false;
  const [qMatrixName, setQMatrixName] = useState('');
  const [qTm, setQTm] = useState<MdlQueryLookup | null>(null);
  // Instrument is multi-select (a Set of instrument names — the value matched against SampleInfo.Instrument).
  const [qInstrSel, setQInstrSel] = useState<Set<string>>(new Set());
  /* Matrix and Method are no longer inputs — the Query Panel derives both from the
     Test pick (prototype 2026-08-17), so the Test lookup lists every TestMethod and
     carries Matrix as one of its columns rather than as a pre-filter. */
  const [qParams, setQParams] = useState<Set<string>>(new Set());
  // Method Version — free text + datalist in the Query Panel (prototype 2026-08-17).
  const [qVersion, setQVersion] = useState('Rev 2');
  // MDL Verification: multi-select QC Type lookup (QCTypeName set), sourced from the selected Test's TestParameter.QCType.
  const [qcTypeSel, setQcTypeSel] = useState<Set<string>>(new Set());
  // QC Category options from dbo.QCCategory; default all selected once loaded.
  const qcCatsQ = useQuery({ queryKey: ['mdl-qccats'], queryFn: ({ signal }) => fetchMdlQcCategories(signal) });
  const [qcCatSel, setQcCatSel] = useState<Set<string>>(new Set());
  const qcCatInit = useRef(false);
  useEffect(() => { if (qcCatsQ.data && !qcCatInit.current) { setQcCatSel(new Set(qcCatsQ.data)); qcCatInit.current = true; } }, [qcCatsQ.data]);
  // QC Types for the selected Test (MDL Verification "QC Type" lookup).
  const qcTypesQ = useQuery({
    queryKey: ['mdl-qctypes', qTm?.testMethodId],
    queryFn: ({ signal }) => fetchMdlQcTypes(qTm!.testMethodId!, signal),
    enabled: !!qTm?.testMethodId,
  });
  // Date range (controlled) — drives the # Replicates count. Initial MDL = calendar year;
  // MDL Verification = rolling trailing 12 months (EPA §4 — recompute from the past 12 months).
  const curYear = new Date().getFullYear();
  const [qDateFrom, setQDateFrom] = useState(`${curYear}-01-01`);
  const [qDateTo, setQDateTo] = useState(`${curYear}-12-31`);
  const dateModeInit = useRef(false);
  useEffect(() => {
    const today = new Date();
    if (qModel === 'verif') {
      const from = new Date(today); from.setFullYear(from.getFullYear() - 1);
      setQDateFrom(isoLocal(from)); setQDateTo(isoLocal(today));
    } else if (dateModeInit.current) {                          // don't clobber the mount default
      setQDateFrom(`${today.getFullYear()}-01-01`); setQDateTo(`${today.getFullYear()}-12-31`);
    }
    dateModeInit.current = true;
  }, [qModel]);
  /* The old single "# Replicates" (distinct AnalyticalBatchID) is superseded by the
     per-bucket counts the batch pickers produce — # Spike Samples / # Blank Samples /
     Total # Replicates — which count SAMPLES, what §2(b) actually requires. The
     /replicate-count endpoint is left in place; this page no longer calls it. */
  /* ── Query Panel · QC batch pickers (Spike Run Pick / Blank Batch Pick) ──
     The runs behind the study are TICKED, not typed, so the dataset the MDL rests
     on is a visible, auditable selection. Sourced from GET /qc-batches, which
     applies the same qualifying predicate the retrieve does — a picker offering a
     batch the retrieve then drops would be worse than no picker. */
  const qcBatchesQ = useQuery({
    queryKey: ['mdl-qcbatches', qTm?.testMethodId, qDateFrom, qDateTo, qModel,
      Array.from(qcCatSel).sort().join(','), Array.from(qcTypeSel).sort().join(','), Array.from(qInstrSel).sort().join(',')],
    queryFn: ({ signal }) => fetchMdlQcBatches(qTm!.testMethodId!, qDateFrom, qDateTo, {
      qcCategories: qModel === 'verif' ? undefined : (qcCatSel.size ? Array.from(qcCatSel) : undefined),
      qcTypes: qModel === 'verif' ? Array.from(qcTypeSel) : undefined,
      instruments: Array.from(qInstrSel),
    }, signal),
    enabled: !!qTm?.testMethodId && !!qDateFrom && !!qDateTo && qDateFrom <= qDateTo
             && (qModel !== 'verif' || qcTypeSel.size > 0),
  });
  const spikeBatches = useMemo(() => (qcBatchesQ.data ?? []).filter((b) => b.kind === 'S'), [qcBatchesQ.data]);
  const blankBatches = useMemo(() => (qcBatchesQ.data ?? []).filter((b) => b.kind === 'B'), [qcBatchesQ.data]);
  // Ticked batch ids. Default = all, so the panel opens on the widest defensible set.
  const [spikePick, setSpikePick] = useState<Set<string>>(new Set());
  const [blankPick, setBlankPick] = useState<Set<string>>(new Set());
  useEffect(() => { setSpikePick(new Set(spikeBatches.map((b) => b.batchId ?? ''))); }, [spikeBatches]);
  useEffect(() => { setBlankPick(new Set(blankBatches.map((b) => b.batchId ?? ''))); }, [blankBatches]);
  /* §2(b) counts REPLICATES, not runs — 5 runs may be 5 or 15 replicates — so the
     yield is summed over the ticked batches and flagged while you are still
     picking, rather than after Load. */
  const pickedCounts = useMemo(() => {
    const sum = (rows: MdlQcBatchOption[], pick: Set<string>) =>
      rows.filter((b) => pick.has(b.batchId ?? '')).reduce((t, b) => t + (b.samples || 0), 0);
    const sPicked = spikeBatches.filter((b) => spikePick.has(b.batchId ?? '')).length;
    const bPicked = blankBatches.filter((b) => blankPick.has(b.batchId ?? '')).length;
    const sReps = sum(spikeBatches, spikePick);
    const bReps = sum(blankBatches, blankPick);
    return { sPicked, bPicked, sTotal: spikeBatches.length, bTotal: blankBatches.length, sReps, bReps, total: sReps + bReps };
  }, [spikeBatches, blankBatches, spikePick, blankPick]);
  const MDL_MIN = 7;

  const [refMdl, setRefMdl] = useState('');
  // Reference MDL options (prior Initial-MDL studies for the selected Method/Test) — MDL Verification.
  const refMdlsQ = useQuery({
    queryKey: ['mdl-reference', qTm?.methodId, qTm?.testName],
    queryFn: ({ signal }) => fetchMdlReferenceMdls(qTm?.methodId ?? null, qTm?.testName ?? null, signal),
    enabled: qModel === 'verif' && (!!qTm?.methodId || !!qTm?.testName),
  });
  const newStudy = useRef<ReturnType<typeof buildQuery> | null>(null);

  /* ── Retrieve checkboxes ── */
  const [spikeChk, setSpikeChk] = useState<boolean[]>(() => RETRIEVE_SPIKES.map(() => true));
  const [blankChk, setBlankChk] = useState<boolean[]>(() => RETRIEVE_BLANKS.map(() => true));
  /* ── Real SDMS retrieve params (TestMethod + date range + QC categories) ── */
  const [retrieveParams, setRetrieveParams] = useState<{ tmId: string; from: string; to: string; cats?: string[]; qcTypes?: string[]; instruments?: string[] } | null>(null);

  /* ── Study detail ── */
  const [studyKey, setStudyKey] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('spikes');
  const fileRef = useRef<HTMLInputElement>(null);

  /* ── Modals ── */
  const [procOpen, setProcOpen] = useState(false);
  const [esigProcOpen, setEsigProcOpen] = useState(false);
  const [esign, setEsign] = useState<null | {
    action: string; meanings: string[]; record: string; onSigned: (s: Signature) => void;
  }>(null);
  const [eMeaning, setEMeaning] = useState('');
  const [eReason, setEReason] = useState('');
  const [ePass, setEPass] = useState('');
  const [eMfa, setEMfa] = useState('');
  const [eAttest, setEAttest] = useState(false);
  const [eError, setEError] = useState('');

  /* ════════ list toolbar actions ════════ */
  const refreshList = () => { setSelected(new Set()); listQ.refetch(); };

  /** Soft-delete the checked studies. Released studies are blocked (server-side too). */
  const doDeleteSelected = async () => {
    const toDelete = rows.filter((r) => selected.has(r.key));
    if (toDelete.length === 0) return;
    const released = toDelete.filter((r) => r.status === 'Released');
    if (released.length) {
      await alertDlg({ title: 'Cannot delete', message: `Released MDL studies cannot be deleted (${released.map((r) => r.mdlId).join(', ')}). Unselect them and try again.`, tone: 'info' });
      return;
    }
    if (!(await confirmDlg({ title: 'Delete studies?', message: `Delete ${toDelete.length} MDL study(ies)? This soft-deletes them from the list; the audit history is retained.`, tone: 'danger', confirmLabel: 'Delete' }))) return;
    try {
      for (const r of toDelete) await deleteMdlStudy(r.key);
      setSelected(new Set());
      await listQ.refetch();
      notify.success(`${toDelete.length} study(ies) deleted.`);
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } }; message?: string })?.response?.data?.error?.message
        || (e as { message?: string })?.message || 'Unknown error';
      await alertDlg({ title: 'Delete failed', message: msg, tone: 'error' });
    }
  };

  /* ════════ query → retrieve ════════ */
  function buildQuery() {
    const ref = refMdl;
    let refVal: number | null = null, refId: string | null = null;
    const m = ref.match(/([0-9]*\.?[0-9]+)\s*(?:µg\/L|mg\/L|ng\/L)/);
    if (m) refVal = parseFloat(m[1] ?? '');
    const mid = ref.match(/MDL-[0-9-]+/); if (mid) refId = mid[0];
    return {
      matrix: qMatrixName, matrixFull: qMatrixName,
      test: qTm?.testName ?? '', testLabel: qTm?.testName ?? '',
      method: qTm?.methodNumber ?? '', instrument: Array.from(qInstrSel).join(', '),
      matrixId: qTm?.matrixId ?? null, testId: qTm?.testMethodId ?? null, methodId: qTm?.methodId ?? null,
      params: [...qParams], modelType: (qModel === 'verif' ? 'verif' : 'initial') as ModelType,
      refVal, refId,
    };
  }
  const openTypeModal = () => { setSelType('initial'); setTypeOpen(true); };
  const proceedToQuery = () => {
    setTypeOpen(false);
    setQModel(selType);
    setView('query');
  };
  // Selecting a TestMethod fills Parameter (all its analytes) + Instrument
  // (auto when exactly one labware, else cleared for the user to pick).
  const selectTm = (tm: MdlQueryLookup) => {
    setQTm(tm);
    setQParams(new Set(tm.parameters ?? []));
    // Auto-select when there is exactly one labware; otherwise leave empty for the user to pick one or more.
    setQInstrSel(new Set(tm.instruments && tm.instruments.length === 1 && tm.instruments[0]?.name ? [tm.instruments[0]!.name!] : []));
    setQcTypeSel(new Set());   // QC Types are Test-scoped — clear the prior selection
    setRefMdl('');             // Reference MDLs are Method-scoped
  };
  const onTestPick = (row: MdlQueryLookup) => selectTm(row);
  const clearQuery = () => { setQMatrixName(''); setQTm(null); setQInstrSel(new Set()); setQParams(new Set()); setQcTypeSel(new Set()); setRefMdl(''); };
  const doRetrieve = () => {
    // Mandatory-field validation before retrieving.
    const missing: string[] = [];
    // Matrix is no longer an input — the Test pick derives it (prototype 2026-08-17).
    if (!qTm?.testMethodId) missing.push('Test');
    if (!qParams.size) missing.push('Parameter (select at least one)');
    if (!qInstrSel.size) missing.push('Instrument');
    if (!qDateFrom) missing.push('Date Range — From');
    if (!qDateTo) missing.push('Date Range — To');
    if (qDateFrom && qDateTo && qDateFrom > qDateTo) missing.push('Date Range — From must be on or before To');
    if (!spikePick.size) missing.push('Spike Run Pick (tick at least one batch)');
    if (qModel !== 'verif' && !blankPick.size) missing.push('Blank Batch Pick (tick at least one batch)');
    // Reference MDL is OPTIONAL on MDL Verification (Ongoing MDL) — without one
    // the study still calculates; only the Ratio-to-Current-MDL check is skipped.
    if (qModel === 'verif' && qcTypeSel.size === 0) missing.push('QC Type (select at least one)');
    if (missing.length) {
      void alertDlg({ title: 'Required fields missing', message: 'Please complete before loading: ' + missing.join(', ') + '.', tone: 'info' });
      return;
    }
    newStudy.current = buildQuery();
    setSpikeChk(RETRIEVE_SPIKES.map(() => true));
    setBlankChk(RETRIEVE_BLANKS.map(() => true));
    if (qTm?.testMethodId) {
      const instruments = Array.from(qInstrSel);   // scope to selected instrument(s) (gap 3.5) — multi-select
      if (qModel === 'verif') {
        // MDL Verification — retrieve by selected QC Types (bucketed via QCRootRole).
        setRetrieveParams({ tmId: qTm.testMethodId, from: qDateFrom, to: qDateTo, qcTypes: Array.from(qcTypeSel), instruments });
      } else {
        const cats = qcCatSel.size ? Array.from(qcCatSel) : ['LFB', 'LRB'];
        setRetrieveParams({ tmId: qTm.testMethodId, from: qDateFrom, to: qDateTo, cats, instruments });
      }
    } else {
      setRetrieveParams(null);
    }
    // Prototype layout (2026-08-17): Load fills the Loaded Samples panel on the
    // RIGHT of this same page. It no longer jumps to Step 2 — Calculate & Save
    // does that, and Step 2 itself is unchanged.
    setQLoaded(true);
  };
  /* Ticked batches are applied to the loaded set here rather than in the query,
     so re-ticking is instant and never re-hits the database. */
  const batchFilter = (row: { qcBatchId: string | null; kind: string | null }) => {
    if (row.kind === 'S') return spikePick.size === 0 ? false : spikePick.has(row.qcBatchId ?? '');
    if (row.kind === 'B') return blankPick.size === 0 ? false : blankPick.has(row.qcBatchId ?? '');
    return false;
  };
  /* Real SDMS retrieve → multi-analyte matrix (MDL-2025-009 layout, live data). */
  const retrieveQ = useQuery({
    queryKey: ['mdl-retrieve', retrieveParams],
    queryFn: async ({ signal }) => {
      const rp = retrieveParams!;
      const opts = { qcCategories: rp.cats, qcTypes: rp.qcTypes, instruments: rp.instruments };
      let from = rp.from;
      let rows = await fetchMdlRetrieve(rp.tmId, from, rp.to, opts, signal);
      let extended = false;
      // MDL Verification §4: if the rolling window has < 7 spiked replicates, pool earlier
      // data — extend backwards in 12-month steps (up to ~5 years) until n ≥ 7 (gap 4.3).
      if (rp.qcTypes && rp.qcTypes.length) {
        let guard = 0;
        while (countSpiked(rows) < 7 && guard < 4) {
          from = isoMinusMonths(from, 12); guard++; extended = true;
          rows = await fetchMdlRetrieve(rp.tmId, from, rp.to, opts, signal);
        }
      }
      return { rows, from, extended, reached: countSpiked(rows) >= 7 };
    },
    enabled: !!retrieveParams,
  });
  // Verification §4: trailing-24-month method-blank exceedance vs the established MDL (gap 2.2).
  const blankExcQ = useQuery({
    queryKey: ['mdl-blank-exc', retrieveParams?.tmId, (retrieveParams?.instruments ?? []).join(','), qModel],
    queryFn: ({ signal }) => fetchMdlBlankExceedance(retrieveParams!.tmId, retrieveParams!.instruments ?? null, signal),
    enabled: qModel === 'verif' && !!retrieveParams?.tmId,
  });
  /* Query Panel · right pane. `qLoaded` gates the Loaded Samples panel; it clears
     whenever the query changes so a stale set can never be read as current. */
  const [qLoaded, setQLoaded] = useState(false);
  /* Keyed on CONTENT, not Set identity. The batch pickers are re-seeded by an effect
     whenever /qc-batches resolves, which mints new Set objects with the same members —
     depending on identity made the pane clear itself moments after Load (the instrument
     change that triggered the refetch landed after the click). */
  const setKey = (s: Set<string>) => Array.from(s).sort().join('');
  const qClearKey = `${qTm?.testMethodId ?? ''}|${qDateFrom}|${qDateTo}|${qModel}`
    + `|${setKey(qcCatSel)}|${setKey(qcTypeSel)}|${setKey(qInstrSel)}|${setKey(spikePick)}|${setKey(blankPick)}`;
  useEffect(() => { setQLoaded(false); }, [qClearKey]);
  const qRows = useMemo(
    () => (retrieveQ.data?.rows ?? []).filter(batchFilter),
    [retrieveQ.data, spikePick, blankPick],
  );
  /* Spike Level / Units are DERIVED from the loaded QC samples, never typed — the
     spike amount is a property of the QC that was run, not of this form. */
  const derivedSpike = useMemo(() => {
    const amts = Array.from(new Set(qRows.map((r) => r.spikeAmount).filter((v): v is number => v != null)));
    const units = Array.from(new Set(qRows.map((r) => r.units).filter((u): u is string => !!u)));
    return {
      level: amts.length === 0 ? '' : amts.length === 1 ? String(amts[0])
           : `${Math.min(...amts)} – ${Math.max(...amts)} (per analyte)`,
      units: units.length ? units.join(', ') : '',
    };
  }, [qRows]);
  // Outlier exclusions (gap 3.4): replicate key → required justification. Cleared on each new retrieve.
  const [excluded, setExcluded] = useState<Map<string, string>>(new Map());
  /* The right pane lists one row per QC SAMPLE, not per analyte result (prototype
     qRenderLoaded): a sample is the unit §2(b) counts, and one sample carries all 8
     analytes. Rows are rolled up here, keeping the analyte count and the result range
     the prototype's Analytes / Result columns show. */
  type QCell = { v: number | null; t: string | null };
  type QSample = {
    key: string; batch: string; code: string;
    /** DISPLAYED in the SysSampleCode column — strictly Samplelogin.SysSampleCode.
     *  Kept separate from `code` (which also feeds the grouping key and may fall
     *  back to the SDMS code for stability) so the column can NEVER show the SDMS
     *  position label, i.e. a QC type, in place of the logged-in sample. */
    sysCode: string;
    kind: 'S' | 'B'; type: string;
    date: string; instr: string; analytes: number; units: string;
    cells: Map<string, QCell>;
  };
  /* Analyte columns follow the TEST's own parameter order (dbo.TestParameter via the
     lookup), not the order rows happen to arrive in — a component whose Sort is NULL
     would otherwise push its analytes to the front and the matrix would reshuffle
     between loads. Anything present in the data but not on the test is appended. */
  const qAnalytes = useMemo(() => {
    const present = new Set(qRows.map((r) => r.analyte ?? '').filter(Boolean));
    const ordered = (qTm?.parameters ?? []).filter((p) => present.has(p));
    for (const a of present) if (!ordered.includes(a)) ordered.push(a);
    return ordered;
  }, [qRows, qTm]);
  const qSamples = useMemo<QSample[]>(() => {
    const by = new Map<string, QSample>();
    for (const r of qRows) {
      const kind: 'S' | 'B' = r.kind === 'S' ? 'S' : 'B';
      const code = r.loginSampleCode || r.sysSampleCode || '—';
      const key = `${r.qcBatchId ?? ''}|${code}|${kind}`;
      let s = by.get(key);
      if (!s) {
        s = {
          key, batch: r.qcBatchId ?? '—', code,
          sysCode: r.loginSampleCode || '—',   // Samplelogin.SysSampleCode only
          kind,
          /* Type = the QC CLASSIFICATION, chosen by MODEL rather than by whichever
             field happens to be populated:
               Initial MDL  -> QC CATEGORY (LFB_MDL / LRB) — how the sample was
                               logged in, which is what drives the Initial retrieve
               Verification -> QC TYPE (MS / MSD / ...) — TestParameter.QCType,
                               which is what drives the Verification retrieve
             The SDMS sample code is deliberately NOT a fallback: it is the sample's
             IDENTITY (shown under SysSampleCode), and leading with it is exactly
             what made this column read as a QC position label, then as a sample
             code once the MDL samples were properly logged in. */
          type: (qModel === 'verif'
                  ? (r.qcTypeName || r.qcCategory)
                  : (r.qcCategory || r.qcTypeName)) || '—',
          date: r.analyzedDate ?? '', instr: r.instrument ?? '—',
          analytes: 0, units: r.units ?? '', cells: new Map<string, QCell>(),
        };
        by.set(key, s);
      }
      s.analytes += 1;
      if (r.analyte) s.cells.set(r.analyte, { v: r.value ?? null, t: r.result ?? null });
    }
    return Array.from(by.values()).sort((a, b) =>
      a.batch.localeCompare(b.batch) || a.kind.localeCompare(b.kind) || a.code.localeCompare(b.code));
    // qModel is a real dependency: it selects whether Type shows the QC category
    // or the QC type. Omitting it would leave Type stale after a model switch.
  }, [qRows, qModel]);
  /* Removed samples (prototype's Action column) — key → reason. A removal needs a
     documented reason, and the tallies score only what SURVIVES. */
  const [qRemoved, setQRemoved] = useState<Map<string, string>>(new Map());
  useEffect(() => { setQRemoved(new Map()); }, [qClearKey]);
  const qKept = useMemo(() => qSamples.filter((s) => !qRemoved.has(s.key)), [qSamples, qRemoved]);
  // Right-pane pagination (prototype footer).
  const [qPage, setQPage] = useState(1);
  const [qPageSize, setQPageSize] = useState(20);
  useEffect(() => { setQPage(1); }, [qSamples, qPageSize]);
  /* Tallies + the §2(b) shortfall reasons, over the SURVIVING samples — this is the
     dataset the MDL would be computed from. */
  const qTallies = useMemo(() => {
    const nS = qKept.filter((s) => s.kind === 'S').length;
    const nB = qKept.filter((s) => s.kind === 'B').length;
    const batches = new Set(qKept.map((s) => s.batch)).size;
    const dates = new Set(qKept.map((s) => s.date.slice(0, 10)).filter(Boolean)).size;
    const short: string[] = [];
    if (nS < MDL_MIN) short.push(`${nS} spiked sample${nS === 1 ? '' : 's'} — ${MDL_MIN} required.`);
    if (qModel !== 'verif' && nB < MDL_MIN) short.push(`${nB} method blank${nB === 1 ? '' : 's'} — ${MDL_MIN} required.`);
    if (batches < 3) short.push(`${batches} batch${batches === 1 ? '' : 'es'} — at least 3 required.`);
    if (dates < 3) short.push(`${dates} calendar date${dates === 1 ? '' : 's'} — at least 3 required.`);
    return { nS, nB, batches, dates, removed: qRemoved.size, short };
  }, [qKept, qModel, qRemoved]);
  const toggleQRemoved = async (s: QSample) => {
    if (qRemoved.has(s.key)) {
      const next = new Map(qRemoved); next.delete(s.key); setQRemoved(next);
      return;
    }
    const reason = await promptDlg({
      title: 'Remove sample from the study',
      message: `${s.sysCode} · ${s.batch} — why is this sample excluded? A documented reason is required (40 CFR 136 App. B).`,
    });
    if (!reason || !reason.trim()) return;
    const next = new Map(qRemoved); next.set(s.key, reason.trim()); setQRemoved(next);
  };
  // Pre-save raw-data attachments — held client-side until the study is created, then uploaded.
  const [preAttach, setPreAttach] = useState<File[]>([]);
  useEffect(() => { setExcluded(new Map()); setPreAttach([]); }, [retrieveParams]);
  const retrieveStudy = useMemo<Study | null>(() => {
    // Reopened-after-rollback: build the editable matrix from the study's own data.
    if (editStudy) {
      return buildRetrieveMatrixStudy(editStudy.rows, {
        matrix: editStudy.meta.matrix, test: editStudy.meta.test, method: editStudy.meta.method,
        instrument: editStudy.meta.instrument, modelType: editStudy.meta.modelType,
        dateFrom: editStudy.meta.dateFrom, dateTo: editStudy.meta.dateTo,
      }, undefined, new Set(excluded.keys()));
    }
    if (!retrieveQ.data || retrieveQ.data.rows.length === 0 || !qTm) return null;
    const flat = retrieveQ.data.rows.map((r) => ({
      analyte: r.analyte ?? '', qcBatchId: r.qcBatchId ?? '', sampleId: r.sampleId ?? '',
      sysSampleCode: r.sysSampleCode ?? '', loginSampleCode: r.loginSampleCode ?? '', qcTypeName: r.qcTypeName ?? '', result: r.result ?? '', kind: r.kind ?? '',
      value: r.value, spikeAmount: r.spikeAmount, currentMdl: r.currentMdl, units: r.units, analyzedDate: r.analyzedDate,
    }));
    const blankExc = (qModel === 'verif' && blankExcQ.data)
      ? Object.fromEntries(blankExcQ.data.filter((b) => b.parameter).map((b) => [b.parameter!, { total: b.totalBlanks, exceed: b.exceedCount }]))
      : undefined;
    return buildRetrieveMatrixStudy(flat, {
      matrix: qTm.matrixName ?? '', test: qTm.testName ?? '', method: qTm.methodNumber ?? '',
      instrument: Array.from(qInstrSel).join(', '), modelType: qModel, dateFrom: qDateFrom, dateTo: qDateTo,
    }, blankExc, new Set(excluded.keys()));
  }, [editStudy, retrieveQ.data, blankExcQ.data, excluded, qTm, qInstrSel, qModel, qDateFrom, qDateTo]);
  const retrieveMatrixHtml = useMemo(() => (retrieveStudy ? renderSpikeTable(retrieveStudy) : ''), [retrieveStudy]);
  const retrieveKpiHtml = useMemo(() => (retrieveStudy ? renderKpis(retrieveStudy) : ''), [retrieveStudy]);
  const [retrieveTab, setRetrieveTab] = useState<'raw' | 'mdl'>('mdl');   // default to the MDL Study tab
  const retrieveTitle = useMemo(() => {
    const q = newStudy.current;
    if (!q) return '';
    const analyte = q.params.length ? (q.params.length === 1 ? q.params[0] : q.params.length + ' analytes') : (q.testLabel || 'Selected analyte');
    const parts = [analyte];
    if (q.method) parts.push(q.method);
    if (q.instrument) parts.push(q.instrument);
    parts.push(q.matrix);
    return parts.join(' · ');
  }, [view]); // recompute when entering retrieve

  /* ════════ study detail ════════  fetched from the API by Oid. ── */
  const openStudy = (key: string) => { setStudyKey(key); setTab('spikes'); setView('study'); };
  const detailQ = useQuery({
    queryKey: ['mdl-detail', studyKey],
    queryFn: ({ signal }) => fetchMdlDetail(studyKey as string, signal),
    enabled: !!studyKey,
  });
  const study: Study | undefined = useMemo(
    () => (detailQ.data ? toStudy(detailQ.data) : undefined),
    [detailQ.data],
  );
  const studyStatus: StudyStatus = study?.status || 'draft';
  // Default the detail tab to the first valid one for this study type (multi mirrors Retrieve).
  useEffect(() => {
    if (!study) return;
    const valid = ['spikes', 'blanks', 'calc', 'docs'];
    if (!valid.includes(tab)) setTab('spikes');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [study]);

  // workflow + buttons derived from status
  const hasData = studyHasData(study);
  const wf = useMemo(() => {
    // Ladder: Query·Retrieve·Calculate·Save(4)·Submit(5)·Validate(6)·Approve(7)·Release(8).
    // activeIdx = the last completed step; the matching action button is shown for the next step.
    let activeIdx = 1, showSubmit = false, showValidate = false, showApprove = false, showRelease = false;
    // Validate / Approve levels may be disabled via ResultEntryDefaultSettings — the workflow
    // then jumps straight to the next enabled level (skipValidate / skipApprove).
    if (studyStatus === 'released') activeIdx = 8;
    else if (studyStatus === 'approved') { activeIdx = 7; showRelease = true; }
    else if (studyStatus === 'validated') { activeIdx = 6; if (skipApprove) showRelease = true; else showApprove = true; }
    else if (studyStatus === 'draft') {   // 'draft' = Submitted (e-signed) → show the next enabled level
      activeIdx = 5;
      if (!skipValidate) showValidate = true;
      else if (!skipApprove) showApprove = true;
      else showRelease = true;
    }
    else { if (hasData) { activeIdx = 4; showSubmit = true; } else activeIdx = 1; }  // 'pending' = Pending Submission
    return { activeIdx, showSubmit, showValidate, showApprove, showRelease };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyKey, studyStatus, hasData, skipValidate, skipApprove]);

  const sigByAction = (action: string): Signature | undefined => study?._signatures?.find((s) => s.action === action);
  const docCompleted = (action: string, fallback: string): string => {
    const s = sigByAction(action);
    return s ? fmtSigDate(s.when) + '  ·  ' + s.name : fallback;
  };

  // memoized study HTML fragments (recompute on study/version/compliance change)
  const studyHtml = useMemo(() => {
    if (!study) return null;
    return {
      kpis: renderKpis(study),
      // Multi-analyte detail mirrors the Retrieve view: "Raw Results" + "MDL Study" (full matrix).
      // Single-analyte keeps the classic Spiked Samples / Method Blanks / Calculation tables.
      spikes: study.multiAnalyte ? renderMatrixSection(study, 'spike') : renderSpikeTable(study),
      blanks: study.multiAnalyte ? renderMatrixSection(study, 'blank') : renderBlankTable(study),
      calc: (study.multiAnalyte ? renderMatrixSection(study, 'summary') : '') + renderCalcRows(study),
      docs: renderDocRows(study),
      sigs: renderSignatures(study),
      attach: renderAttachments(study),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyKey, study, studyStatus]);

  /* ════════ e-signature engine ════════ */
  // Authority is enforced SERVER-SIDE — MdlStudyWriter.TransitionAsync validates the
  // current status and the separation-of-duties rule (the submitter cannot also
  // validate/approve/release). The old client-side role gate used prototype role names
  // ('Analyst'/'Reviewer'/'QA Manager'/'Lab Director') that don't map to the real XAF
  // roles, so it falsely blocked legitimate users (e.g. an Administrator who arrives
  // without the isAdmin flag set). The modal still shows the expected authority as
  // guidance (ACTION_REQ); we never hard-block in the UI — the server is authoritative.
  const requestSignature = (opts: { action: string; meaning: string; meanings?: string[]; record?: string; onSigned: (s: Signature) => void }) => {
    setEMeaning(opts.meaning); setEReason(''); setEPass(''); setEMfa(''); setEAttest(false); setEError('');
    setEsign({ action: opts.action, meanings: opts.meanings || [opts.meaning], record: opts.record || (study ? study.mdlId : '(new study)'), onSigned: opts.onSigned });
  };
  const cancelSignature = () => setEsign(null);
  const confirmSignature = () => {
    if (!esign) return;
    const part11 = compliance === 'part11';
    if (part11 && ESIGN_REQUIRE_CREDENTIALS) {
      if (!ePass) { setEError('Password is required for a 21 CFR Part 11 signature.'); return; }
      if (!/^\d{6}$/.test(eMfa.trim())) { setEError('Enter the 6-digit authenticator (MFA) code.'); return; }
    }
    if (!eAttest) { setEError('You must check the attestation box to apply your signature.'); return; }
    const sig: Signature = {
      action: esign.action, meaning: eMeaning, reason: eReason,
      name: currentUser.name, userId: currentUser.id, role: currentUser.role,
      mode: compliance, mfa: part11, when: new Date(),
    };
    const cb = esign.onSigned;
    setEsign(null);
    cb(sig);
  };

  // Submit (2026-06-27) — the first e-signature: Pending Submission → Submitted.
  const doSubmit = () => requestSignature({ action: 'Submit', meaning: 'Authored & Submitted', meanings: ['Authored & Submitted', 'Submitted for review'], onSigned: (sig) => { void applyWorkflow('Submit', sig); } });
  const doValidate = () => requestSignature({ action: 'Validate', meaning: 'Reviewed & Validated', meanings: ['Reviewed & Validated', 'Reviewed'], onSigned: (sig) => { void applyWorkflow('Validate', sig); } });
  const doApprove = () => requestSignature({ action: 'Approve', meaning: 'Approved', meanings: ['Approved', 'Approved with comment'], onSigned: (sig) => { void applyWorkflow('Approve', sig); } });
  const doRelease = () => requestSignature({ action: 'Release', meaning: 'Released', meanings: ['Released'], onSigned: (sig) => { void applyWorkflow('Release', sig); } });
  // C5 — Roll Back returns the study to entry level (Submitted) AND reopens it in the editable
  // Calculate & Save view with its own data, so the author can revise and re-save (feedback 2026-06-26).
  const doRollback = () => requestSignature({ action: 'Rollback', meaning: 'Rolled back for revision', meanings: ['Rolled back for revision', 'Returned to author'], onSigned: (sig) => { void rollbackAndReopen(sig); } });
  /** Reopen the currently-open study in the editable Calculate & Save view, rebuilding the
   *  matrix from its own (unchanged) replicates. Used after Rollback AND by the detail-view
   *  Edit button (so a rolled-back / entry-level study can be revised any time it is reopened,
   *  not only in the transient moment right after Rollback). Returns false if data isn't ready. */
  const openEditorFromDetail = (): boolean => {
    if (!studyKey || !detailQ.data) return false;
    const det = detailQ.data;
    const resById = new Map(det.results.map((r) => [r.oid, r]));
    const rows: EditFlatRow[] = det.replicates.map((rp) => {
      const r = resById.get(rp.resultOid);
      return {
        analyte: r?.parameter ?? '', qcBatchId: rp.batchId ?? '', sampleId: rp.sampleId ?? '',
        sysSampleCode: rp.sysSampleCode ?? '', qcTypeName: '',
        result: rp.resultText ?? (rp.measuredValue != null ? String(rp.measuredValue) : ''), kind: rp.kind ?? '',
        value: rp.measuredValue, spikeAmount: r?.spikeConc ?? null, currentMdl: r?.currentMdl ?? null,
        units: r?.units ?? '', analyzedDate: rp.analyzedDate ?? '',
      };
    });
    const h = det.header;
    const model: ModelType = h.modelType === 1 ? 'verif' : 'initial';
    setQModel(model);
    newStudy.current = {
      matrix: h.matrix ?? '', matrixFull: h.matrix ?? '', test: h.test ?? '', testLabel: h.test ?? '',
      method: h.method ?? '', instrument: h.instrument ?? '', matrixId: null, testId: null, methodId: null,
      params: det.results.map((r) => r.parameter ?? '').filter(Boolean), modelType: model, refVal: null, refId: null,
    };
    setExcluded(new Map()); setPreAttach([]);
    setEditStudy({ oid: studyKey, rows, meta: { matrix: h.matrix ?? '', test: h.test ?? '', method: h.method ?? '', instrument: h.instrument ?? '', modelType: model, dateFrom: '', dateTo: '' } });
    setView('retrieve');
    return true;
  };
  const rollbackAndReopen = async (sig: Signature) => {
    if (!studyKey || !detailQ.data) return;
    try {
      await transitionMdlStudy(studyKey, 'Rollback', {
        action: sig.action, meaning: sig.meaning, reason: sig.reason || null, mode: sig.mode,
        mfa: !!sig.mfa, name: sig.name || null, role: sig.role || null, userId: sig.userId || null,
      });
      await detailQ.refetch();         // status is now Pending Submission — refresh the cache (same key)
      openEditorFromDetail();          // drop straight into the editable Calculate & Save view
      await listQ.refetch();
      notify.success('Rolled back to Pending Submission.');
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } }; message?: string })?.response?.data?.error?.message
        || (e as { message?: string })?.message || 'Unknown error';
      await alertDlg({ title: 'Roll Back failed', message: msg, tone: 'error' });
    }
  };
  // Opening a Pending Submission study shows its REVIEW page (stepper at Submit + the Submit button),
  // NOT the editor. To revise before submitting, the review page has an "Edit (Calculate & Save)"
  // button that reopens the editable matrix. (2026-06-29 — feedback: open should show Submit, and the
  // editor must keep its Re-Retrieve button.)
  const doEditStudy = () => { if (!openEditorFromDetail()) notify.info('Study data is still loading — please try again in a moment.'); };

  /** Push each analyte's Final MDL into dbo.TestParameter.MDL (QCType=Sample).
   *  `names` (C12) limits the write to the chosen parameters; omit = all. */
  const runApplyMdl = async (names?: string[]) => {
    if (!studyKey) return;
    try {
      const res = await applyMdl(studyKey, names);
      await detailQ.refetch();
      notify.success(`MDL applied to ${res.rows} Sample TestParameter row(s) across ${res.analytes} parameter(s).`);
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } }; message?: string })?.response?.data?.error?.message
        || (e as { message?: string })?.message || 'Unknown error';
      await alertDlg({ title: 'Apply MDL failed', message: msg, tone: 'error' });
    }
  };
  /** "Do you want to update the new MDL value in the Sample Parameters?" — used
   *  post-Release and by the Apply MDL button. A multi-analyte study opens a
   *  parameter picker (C12); single-analyte applies directly after a confirm. */
  const applyMdlNow = async () => {
    if (!studyKey || !study) return;
    const names = (study.analytes || []).map((a) => a.name).filter(Boolean) as string[];
    if (names.length > 1) { setApplyPick({ names, sel: new Set(names) }); return; }
    if (!(await confirmDlg({ title: 'Apply MDL?', message: 'Do you want to update the new MDL value in the Sample Parameters?' }))) return;
    await runApplyMdl();
  };

  /** Persist a workflow transition (Validate/Approve/Release/Rollback) via the API,
   *  then refresh the detail + list so the new status, signature and counts show. */
  const applyWorkflow = async (action: 'Submit' | 'Validate' | 'Approve' | 'Release' | 'Rollback', sig: Signature) => {
    if (!studyKey) return;
    try {
      await transitionMdlStudy(studyKey, action, {
        action: sig.action, meaning: sig.meaning, reason: sig.reason || null, mode: sig.mode,
        mfa: !!sig.mfa, name: sig.name || null, role: sig.role || null, userId: sig.userId || null,
      });
      await Promise.all([detailQ.refetch(), listQ.refetch()]);
      notify.success(
        action === 'Submit' ? 'Study submitted.' : action === 'Validate' ? 'Study validated.'
        : action === 'Approve' ? 'Study approved.' : action === 'Release' ? 'Study released.'
        : 'Rolled back to Pending Submission.');
      // After Release, offer to push the new MDL into the Sample TestParameters.
      if (action === 'Release') await applyMdlNow();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } }; message?: string })?.response?.data?.error?.message
        || (e as { message?: string })?.message || 'Unknown error';
      await alertDlg({ title: action + ' failed', message: msg, tone: 'error' });
    }
  };

  /* ════════ calculate & save (new study) — persists via the API; the MDL is
   *  computed server-side and the MdlId minted race-safe.  Save no longer captures an
   *  e-signature (2026-06-27): the study is created at "Pending Submission" after a plain
   *  confirm; the first e-signature is taken later at Submit. ── */
  const [saving, setSaving] = useState(false);
  const doCalculate = async () => {
    if (!newStudy.current) return;
    // Enforce 40 CFR 136 App. B minimums before saving (gap 3.2 + 3.1/3.3) — allow override with acknowledgement.
    const c = retrieveStudy?._compliance;
    if (c) {
      const fails: string[] = [];
      if (!c.minSpikeOk) fails.push(`Only ${c.nSpike} spiked replicates (minimum 7).`);
      if (qModel !== 'verif' && !c.minBlankOk) fails.push(`Only ${c.nBlank} method blanks (minimum 7).`);
      if (!c.spreadOk) fails.push(`Spread is ${c.batches} batch(es) / ${c.dates} date(s) (minimum 3 batches on 3 dates).`);
      // C1: spike level vs the (estimated) MDL is informational only — not a save blocker.
      if (qModel === 'verif' && !c.cadenceOk) fails.push('One or more quarters have fewer than 2 MDL spikes (§3 cadence).');
      if (fails.length && !(await confirmDlg({ title: 'Requirements not met', message: 'This study does not meet all 40 CFR 136 App. B requirements: ' + fails.join(' ') + ' Save anyway?', tone: 'danger', confirmLabel: 'Save anyway' }))) return;
    }
    // Save directly (no confirm dialog, no e-signature — captured at Submit). The study is
    // created/updated at "Pending Submission". A synthetic record stamps who saved it (CreatedByName).
    const saveSig: Signature = {
      action: 'Save', meaning: 'Saved (Pending Submission)', reason: '',
      name: currentUser.name, userId: currentUser.id, role: currentUser.role,
      mode: compliance, mfa: false, when: new Date(),
    };
    void saveNewStudy(saveSig);
  };
  const saveNewStudy = async (sig: Signature) => {
    const q = newStudy.current;
    if (!q || saving) return;
    setSaving(true);
    try {
      const sigPayload = { action: sig.action, meaning: sig.meaning, reason: sig.reason || null, mode: sig.mode, mfa: !!sig.mfa, name: sig.name || null, role: sig.role || null, userId: sig.userId || null };

      // Edit mode (Pending Submission study reopened for revision) — UPDATE in place (same MdlId, stays Pending),
      // then land on the study's review page (Submit step), where the author Submits.
      if (editStudy) {
        const flat = editStudy.rows.filter((r) => !excluded.has(replicateKey({ qcType: r.qcTypeName ?? '', sample: baseSysCode(r.sysSampleCode) || r.sampleId || '', id: r.qcBatchId ?? '' })));
        const analytes = buildRetrieveAnalytePayload(flat).filter((a) => a.spikes.length > 0 || a.blanks.length > 0);
        const res = await updateMdlStudyMulti(editStudy.oid, {
          matrix: editStudy.meta.matrix, test: editStudy.meta.test, method: editStudy.meta.method, instrument: editStudy.meta.instrument,
          matrixId: null, testId: null, methodId: null,
          modelType: editStudy.meta.modelType === 'verif' ? 1 : 0, methodVersion: 'Rev 2',
          dateFrom: editStudy.meta.dateFrom || null, dateTo: editStudy.meta.dateTo || null,
          analytes, signature: sigPayload,
          exclusions: [...excluded.entries()].map(([key, reason]) => { const [qcType, sysSampleCode, qcBatchId] = key.split('|'); return { qcType: qcType || null, sysSampleCode: sysSampleCode || null, qcBatchId: qcBatchId || null, reason }; }),
          qcCategory: editStudy.meta.modelType === 'verif' ? null : 'LFB, LRB',
          qcType: null,
        });
        for (const f of preAttach) { try { await uploadMdlAttachment(res.id, f); } catch { /* surfaced on the detail */ } }
        setEditStudy(null);
        await Promise.all([listQ.refetch(), detailQ.refetch()]);   // refresh status (same key) so the review shows Submit
        notify.success('Study saved.');
        openStudy(res.id);   // review page — stepper at Submit
        return;
      }

      // Multi-analyte path — persist one result per retrieved analyte (real /retrieve data).
      if (retrieveStudy && retrieveQ.data && retrieveQ.data.rows.length > 0) {
        const flat = retrieveQ.data.rows
          // Drop excluded outlier replicates so the persisted MDL matches the displayed one (gap 3.4).
          .filter((r) => !excluded.has(replicateKey({ qcType: r.qcTypeName ?? '', sample: baseSysCode(r.sysSampleCode) || r.sampleId || '', id: r.qcBatchId ?? '' })))
          .map((r) => ({
            analyte: r.analyte ?? '', qcBatchId: r.qcBatchId ?? '', sampleId: r.sampleId ?? '',
            sysSampleCode: r.sysSampleCode ?? '', loginSampleCode: r.loginSampleCode ?? '', qcTypeName: r.qcTypeName ?? '', result: r.result ?? '', kind: r.kind ?? '',
            value: r.value, spikeAmount: r.spikeAmount, currentMdl: r.currentMdl, units: r.units, analyzedDate: r.analyzedDate,
          }));
        const analytes = buildRetrieveAnalytePayload(flat).filter((a) => a.spikes.length > 0 || a.blanks.length > 0);
        const res = await createMdlStudyMulti({
          matrix: q.matrix, test: q.testLabel || q.test || null, method: q.method || null, instrument: q.instrument || null,
          matrixId: q.matrixId, testId: q.testId, methodId: q.methodId,
          modelType: q.modelType === 'verif' ? 1 : 0, methodVersion: 'Rev 2', dateFrom: qDateFrom, dateTo: qDateTo,
          analytes, signature: sigPayload,
          // Excluded outliers — audited at Save (who/when/reason/value), gap 3.4.
          exclusions: [...excluded.entries()].map(([k, reason]) => {
            const [qcType, sysSampleCode, qcBatchId] = k.split('|');
            return { qcType: qcType || null, sysSampleCode: sysSampleCode || null, qcBatchId: qcBatchId || null, reason };
          }),
          // Retrieved QC selection — shown comma-separated in the study Documentation.
          qcCategory: q.modelType === 'verif' ? null : (qcCatSel.size ? Array.from(qcCatSel).join(', ') : 'LFB, LRB'),
          qcType: q.modelType === 'verif' ? (qcTypeSel.size ? Array.from(qcTypeSel).join(', ') : null) : null,
        });
        // Upload any raw-data files staged before save, now that the study exists (req c).
        for (const f of preAttach) { try { await uploadMdlAttachment(res.id, f); } catch { /* surfaced on the detail */ } }
        await listQ.refetch();
        notify.success('Study saved.');
        openStudy(res.id);   // review page — stepper at Submit
        return;
      }

      const spikes = RETRIEVE_SPIKES.filter((_, i) => spikeChk[i]).map((s) => ({ batch: s.batch, sample: s.sample, date: s.date, spikeConc: s.spike, result: s.result }));
      const blanks = RETRIEVE_BLANKS.filter((_, i) => blankChk[i]).map((b) => ({ batch: b.batch, sample: b.sample, date: b.date, result: b.result, numerical: true, note: b.note ?? null }));
      const analyte: string | null = q.params.length ? (q.params[0] ?? null) : (q.testLabel || null);
      const res = await createMdlStudy({
        matrix: q.matrix, test: analyte, method: q.method || null, instrument: q.instrument || null,
        matrixId: q.matrixId, testId: q.testId, methodId: q.methodId,
        modelType: q.modelType === 'verif' ? 1 : 0, methodVersion: 'Rev 2', spikeLevel: null, analyte, units: 'mg/L',
        referenceMdl: q.refVal ?? null, spikes, blanks,
        signature: { action: sig.action, meaning: sig.meaning, reason: sig.reason || null, mode: sig.mode, mfa: !!sig.mfa, name: sig.name || null, role: sig.role || null, userId: sig.userId || null },
      });
      for (const f of preAttach) { try { await uploadMdlAttachment(res.id, f); } catch { /* surfaced on the detail */ } }
      await listQ.refetch();
      notify.success('Study saved.');
      openStudy(res.id); // review page — stepper at Submit
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } }; message?: string })?.response?.data?.error?.message
        || (e as { message?: string })?.message || 'Unknown error';
      await alertDlg({ title: 'Save failed', message: msg, tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  /* ════════ attachments (IFileStore-backed; upload / download / remove) ════════ */
  const onAttachFiles = async (files: FileList | null) => {
    if (!files || !studyKey || files.length === 0) return;
    try {
      for (const f of Array.from(files)) await uploadMdlAttachment(studyKey, f);
      await detailQ.refetch();
      notify.success('Attachment uploaded.');
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } }; message?: string })?.response?.data?.error?.message
        || (e as { message?: string })?.message || 'Unknown error';
      await alertDlg({ title: 'Upload failed', message: msg, tone: 'error' });
    }
  };
  const onAttachClick = (e: React.MouseEvent) => {
    if (!studyKey) return;
    const dl = (e.target as HTMLElement).closest('[data-attach-download]');
    if (dl) {
      void downloadMdlAttachment(studyKey, dl.getAttribute('data-attach-download')!, dl.textContent || 'attachment');
      return;
    }
    const rm = (e.target as HTMLElement).closest('[data-attach-remove]');
    if (rm) {
      const attId = rm.getAttribute('data-attach-remove')!;
      void (async () => {
        if (!(await confirmDlg({ title: 'Remove attachment?', message: 'Remove this attachment?', tone: 'danger', confirmLabel: 'Remove' }))) return;
        try { await deleteMdlAttachment(studyKey, attId); await detailQ.refetch(); notify.success('Attachment removed.'); }
        catch (e) {
          const msg = (e as { response?: { data?: { error?: { message?: string } } }; message?: string })?.response?.data?.error?.message
            || (e as { message?: string })?.message || 'Unknown error';
          await alertDlg({ title: 'Delete failed', message: msg, tone: 'error' });
        }
      })();
    }
  };

  /* ════════ PDF report ════════ */
  const [reportChooser, setReportChooser] = useState(false);
  const generatePdf = async (layout: 'standard' | 'grid' = 'standard') => {
    setReportChooser(false);
    if (!study) { notify.info('Open a study first, then generate its report.'); return; }
    // Open the print window synchronously (in the click gesture) so the pop-up isn't blocked;
    // if the user then cancels the watermark confirm, close it.
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) { notify.warning('Please allow pop-ups for this page to generate the PDF report.'); return; }
    if (studyStatus !== 'released') {
      const ok = await confirmDlg({
        title: 'Uncontrolled draft',
        message: 'This study is ' + statusTitle(studyStatus) + ' — not yet Released. The report will be issued as an UNCONTROLLED COPY watermarked "' + watermarkLabel(studyStatus) + '" and is NOT valid for distribution. Complete Validate → Approve → Release for the final controlled report. Generate the watermarked draft anyway?',
        tone: 'danger', confirmLabel: 'Generate draft',
      });
      if (!ok) { w.close(); return; }
    }
    w.document.open(); w.document.write(buildReportHtml(study, studyStatus, layout)); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch { /* ignore */ } }, 400);
  };

  /* ════════ Shell docking (BO UI Standard §5) ════════
     The Dynamic-Island stepper docks CENTERED in the shared TOP BAR (§5) for every MDL
     workflow view — Query / Retrieve / Study — never inline in the page. The compliance +
     signing badges render on the form TITLE ROW (before Procedure Rules), not the top bar. */
  const { setTopbarCenter } = useBanner();
  // §13 two-tier notifications: blocking dialogs (confirmDlg / alertDlg) + transient banner (notify).
  const notify = useNotify();
  // `prompt` backs the Loaded-Samples remove action — an exclusion needs a documented
  // reason (40 CFR 136 App. B), captured through the ONE shared dialog (§18.6).
  const { confirm: confirmDlg, alert: alertDlg, prompt: promptDlg } = useConfirm();
  // The stepper hides disabled levels (Validate / Approve) per ResultEntryDefaultSettings.
  const wfSteps = WF_STEPS.filter((s) =>
    !(skipValidate && /Validate/i.test(s.label)) && !(skipApprove && /Approve/i.test(s.label)));
  const currentStepLabel = (() => {
    if (view === 'query') return 'Query';
    if (view === 'retrieve') return 'Load';
    if (view !== 'study') return null;
    if (studyStatus === 'released') return '__done__';
    if (studyStatus === 'approved') return 'Release';
    if (studyStatus === 'validated') return skipApprove ? 'Release' : 'Approve';
    if (studyStatus === 'draft') return skipValidate ? (skipApprove ? 'Release' : 'Approve') : 'Validate';
    return hasData ? 'Submit' : 'Load';
  })();
  const islandIdx = currentStepLabel == null ? -1
    : currentStepLabel === '__done__' ? wfSteps.length
    : (() => { const i = wfSteps.findIndex((s) => s.label.replace(/^\s*\d+\.\s*/, '') === currentStepLabel); return i < 0 ? wfSteps.length : i; })();

  useEffect(() => {
    if (islandIdx < 0) { setTopbarCenter(null); return; }
    setTopbarCenter(<WorkflowStepper steps={wfSteps} activeIdx={islandIdx} />);
    return () => setTopbarCenter(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [islandIdx, skipValidate, skipApprove, setTopbarCenter]);

  // Compliance + signing badges — placed on the form title row (dp-title-actions), before Procedure Rules.
  const mdlBadges = (
    <>
      <span className="mdl-cmpl-badge"><i className="ti ti-certificate" /> 40 CFR 136 App. B Rev. 2</span>
      <button className="tb-btn" onClick={() => setCompliance((c) => (c === 'part11' ? 'iso' : 'part11'))} title="Switch signing standard">
        <i className="ti ti-shield-lock" /> Signing: {compliance === 'part11' ? '21 CFR Part 11' : 'ISO 17025'}
      </button>
    </>
  );

  const meaningRecord = esign ? esign.record : '—';
  const part11 = compliance === 'part11';
  const attestText = 'I certify that I am ' + currentUser.name + ' (' + currentUser.role + '), and that applying my electronic signature to record ' + meaningRecord + ' constitutes my signature with the meaning "' + eMeaning + '".';

  // Phase 2.3 — MDL Studies action keys (bespoke multi-view page — CLAUDE.md
  // standing rule 14, §18.6, ADR-0013).  Gated per view, mirroring the wf-actions
  // buttons; placed before the single return (rules of hooks).
  const mdlKeys: HotkeyDef[] = [];
  if (view === 'query') {
    mdlKeys.push({ combo: 'alt+l', label: 'Load', group: 'MDL Studies', order: 1, run: () => doRetrieve() });
    mdlKeys.push({ combo: 'mod+s', label: 'Calculate & Save', group: 'MDL Studies', order: 2, run: () => { void doCalculate(); }, disabled: !qLoaded || qRows.length === 0 });
    mdlKeys.push({ combo: 'alt+c', label: 'Reset', group: 'MDL Studies', order: 3, run: () => clearQuery() });
  }
  if (view === 'retrieve') {
    mdlKeys.push({ combo: 'alt+r', label: 'Refresh', group: 'MDL Studies', order: 1, run: () => { void retrieveQ.refetch(); } });
    mdlKeys.push({ combo: 'mod+s', label: 'Calculate & Save', group: 'MDL Studies', order: 2, run: () => { void doCalculate(); } });
  }
  if (view === 'study') {
    mdlKeys.push({ combo: 'alt+g', label: 'PDF Report', group: 'MDL Studies', order: 1, disabled: !study, run: () => setReportChooser(true) });
    mdlKeys.push({ combo: 'alt+e', label: 'Edit (Calculate & Save)', group: 'MDL Studies', order: 2, disabled: studyStatus !== 'pending', run: () => doEditStudy() });
    mdlKeys.push({ combo: 'alt+b', label: 'Roll Back', group: 'MDL Studies', order: 3, disabled: !(studyStatus === 'draft' || studyStatus === 'validated' || studyStatus === 'approved'), run: () => doRollback() });
    mdlKeys.push({ combo: 'alt+u', label: 'Submit', group: 'MDL Studies', order: 4, disabled: !wf.showSubmit, run: () => doSubmit() });
    mdlKeys.push({ combo: 'alt+v', label: 'Validate', group: 'MDL Studies', order: 5, disabled: !wf.showValidate, run: () => doValidate() });
    mdlKeys.push({ combo: 'alt+a', label: 'Approve', group: 'MDL Studies', order: 6, disabled: !wf.showApprove, run: () => doApprove() });
    mdlKeys.push({ combo: 'alt+y', label: 'Release', group: 'MDL Studies', order: 7, disabled: !wf.showRelease, run: () => doRelease() });
    mdlKeys.push({ combo: 'alt+shift+a', label: 'Apply MDL', group: 'MDL Studies', order: 8, disabled: studyStatus !== 'released', run: () => { void applyMdlNow(); } });
  }
  if (view !== 'list') {
    mdlKeys.push({ combo: 'esc', label: 'Back to List', group: 'MDL Studies', order: 9, allowInInput: false, run: () => setView('list') });
  }
  useHotkeys(mdlKeys);

  return (
    <div className="mdl-root">
      <div className="content">

        {/* ════════ LIST ════════ */}
        {view === 'list' && (
          <div className="page">
            {/* Header padded to match the .dp-scroll body (16px) so the title,
                toolbar, KPIs and grid all share ONE left edge (fix 2026-07-08). */}
            <div className="mdl-list-hdr">
              <FormTitle pageId="mdlstudies" subtitle="Method Detection Limit — 40 CFR 136 Appendix B, Revision 2" loadMs={listMs} loading={listQ.isFetching} />
              <AppToolbar
                onNew={openTypeModal} newLabel="New"
                onDelete={doDeleteSelected} deleteDisabled={selected.size === 0}
                onRefresh={refreshList} refreshing={listQ.isFetching}
                exportData={{ filename: 'MDL-Studies', ...gridExportMatrix(rows, MDL_LIST_COLUMNS) }}
              />
            </div>

            <div className="dp-scroll">
              <div className="kpi-row">
                <div className="kpi"><div className="kpi-bar blue" /><div className="kpi-lbl">Total Studies</div><div className="kpi-val blue">{kpi.total}</div><div className="kpi-sub">All time</div></div>
                <div className="kpi"><div className="kpi-bar green" /><div className="kpi-lbl">Active MDLs</div><div className="kpi-val green">{kpi.active}</div><div className="kpi-sub">Released &amp; effective</div></div>
                <div className="kpi"><div className="kpi-bar amber" /><div className="kpi-lbl">Due Verification</div><div className="kpi-val amber">{kpi.due}</div><div className="kpi-sub">Within 90 days</div></div>
                <div className="kpi"><div className="kpi-bar red" /><div className="kpi-lbl">Overdue</div><div className="kpi-val red">{kpi.overdue}</div><div className="kpi-sub">Past Next Due date</div></div>
                {/* token-exempt: #534AB7 is the Pending-Approval KPI category color (data-viz) — no purple token exists */}
                <div className="kpi"><div className="kpi-bar blue" style={{ background: '#534AB7' }} /><div className="kpi-lbl">Pending Approval</div><div className="kpi-val" style={{ color: '#534AB7' }}>{kpi.pending}</div><div className="kpi-sub">Awaiting sign-off</div></div>
              </div>

              {listQ.isError && !listQ.isLoading ? (
                <div className="alert-warning" style={{ margin: '8px 0' }}>Failed to load MDL studies. <a style={{ color: 'var(--ai-blue)', cursor: 'pointer' }} onClick={() => listQ.refetch()}>Retry</a></div>
              ) : (
                <AppDataGrid<ListRow>
                  rows={rows}
                  columns={MDL_LIST_COLUMNS}
                  getRowId={(r) => r.key}
                  loading={listQ.isLoading}
                  onRowClick={(r) => openStudy(r.key)}
                  selectable
                  selectedIds={selected}
                  onSelectionChange={setSelected}
                  emptyText="No matching records"
                />
              )}
            </div>
          </div>
        )}

        {/* ════════ QUERY ════════ */}
        {view === 'query' && (
          <div className="page">
            <div className="dp-topbar">
              <div className="dp-breadcrumb">
                <a onClick={() => setView('list')}>MDL Studies</a>
                <i className="ti ti-chevron-right" />
                <span>{qModel === 'verif' ? 'New MDL Verification' : 'New Initial MDL Study'}</span>
              </div>
              <div className="dp-title-row">
                <div className="dp-title">{qModel === 'verif' ? 'New Study — MDL Verification' : 'New Study — Initial MDL'}</div>
                <div className="dp-title-actions">
                  {mdlBadges}
                  <span className="audit-chip" onClick={() => setProcOpen(true)}><i className="ti ti-certificate" /> Procedure Rules</span>
                </div>
              </div>
              <div className="dp-sub">Define the study parameters, then retrieve the matching SDMS raw data.</div>
            </div>
            <div className="dp-scroll qp-scroll">
              <div className="wf-actions">
                <button className="xaf-tb-btn" onClick={clearQuery}><i className="ti ti-eraser" /> Reset</button>
                <button className="xaf-tb-btn" onClick={() => setView('list')}><i className="ti ti-arrow-left" /> Back to List</button>
                <button className="xaf-tb-btn primary" onClick={doRetrieve}><span aria-hidden="true">⬇️</span> Load</button>
                <button className="xaf-tb-btn" onClick={doCalculate} disabled={!qLoaded || qRows.length === 0}><i className="ti ti-calculator" /> Calculate &amp; Save</button>
              </div>
              <div className="hint"><b>Step 1 · Define Parameters.</b> Pick a <b>Test</b>, choose the <b>QC batches</b>, then <b>Load</b>.</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 12px' }}>
                <span style={{ fontSize: 11, color: 'var(--ai-muted)', fontWeight: 'bold' }}>Model Type:</span>
                <div style={{ display: 'flex', border: '1px solid var(--ai-orange)', borderRadius: 4, overflow: 'hidden' }}>
                  <button onClick={() => setQModel('initial')} style={{ padding: '5px 18px', fontSize: 12, fontFamily: 'inherit', background: qModel === 'verif' ? '#fff' : 'var(--ai-orange)', color: qModel === 'verif' ? 'var(--ai-muted)' : '#fff', fontWeight: qModel === 'verif' ? 'normal' : 'bold', border: 'none', cursor: 'pointer', borderRight: '1px solid var(--ai-orange)' }}>Initial MDL</button>
                  <button onClick={() => setQModel('verif')} style={{ padding: '5px 18px', fontSize: 12, fontFamily: 'inherit', background: qModel === 'verif' ? 'var(--ai-orange)' : '#fff', color: qModel === 'verif' ? '#fff' : 'var(--ai-muted)', fontWeight: qModel === 'verif' ? 'bold' : 'normal', border: 'none', cursor: 'pointer' }}>MDL Verification</button>
                </div>
              </div>

              {/* Prototype layout (2026-08-17): LEFT = the filters, RIGHT = the loaded
                  samples reviewed before calculating — the same filter-pane /
                  result-pane split the Control Chart uses. */}
              <div className="qp-split">

                {/* ════ LEFT · QUERY PANEL ════ */}
                <div className="card" style={{ overflow: 'visible' }}>
                  <div className="card-hdr">
                    <div className="card-title"><i className="ti ti-adjustments-horizontal" /> Query Panel</div>
                    <div className="card-actions"><div className="compliance-badge"><i className="ti ti-certificate" /> 40 CFR 136 App. B</div></div>
                  </div>
                  <div className="qp-rows">

                    {/* Test drives everything below it — Method, Matrix, parameters,
                        instruments and the batch pools are all read off this pick. */}
                    <div className={`qp-row${!qTm ? ' qp-need qp-need-lbl' : ''}`}>
                      <div className="form-label">Test <span className="qp-req">*</span></div>
                      <GridLookup<MdlQueryLookup>
                        rows={lookupsQ.data ?? []} value={qTm} onPick={onTestPick}
                        rowKey={(t) => t.testMethodId ?? (t.testName ?? '')} display={(t) => t.testName ?? ''}
                        search={(t) => `${t.matrixName ?? ''} ${t.testName ?? ''} ${t.methodNumber ?? ''}`}
                        columns={[
                          { header: 'Matrix', cell: (t) => t.matrixName },
                          { header: 'Test', cell: (t) => t.testName },
                          { header: 'Method', cell: (t) => t.methodNumber },
                          { header: 'Analytes', cell: (t) => <span className="qp-lk-rec">{t.parameters?.length ?? 0}</span> },
                        ]}
                        placeholder="— Select Test —" popupClassName="qp-lk-pop"
                        searchPlaceholder="Search matrix, test or method…" />
                    </div>

                    <div className="qp-row">
                      <div className="form-label">Method</div>
                      <div className={`qp-ro${qTm?.methodNumber ? '' : ' empty'}`}>{qTm?.methodNumber || '— from Test —'}</div>
                    </div>
                    <div className="qp-row">
                      <div className="form-label">Matrix</div>
                      <div className={`qp-ro${qTm?.matrixName ? '' : ' empty'}`}>{qTm?.matrixName || '— from Test —'}</div>
                    </div>
                    <div className="qp-row">
                      <div className="form-label">Version</div>
                      <input className="form-control" type="text" list="q-version-list" value={qVersion}
                             onChange={(e) => setQVersion(e.target.value)} placeholder="e.g. Rev 2" />
                      <datalist id="q-version-list"><option>Rev 1</option><option>Rev 2</option><option>Rev 3</option></datalist>
                    </div>

                    <div className={`qp-row${qTm && qParams.size === 0 ? ' qp-need qp-need-lbl' : ''}`}>
                      <div className="form-label">Parameter <span className="qp-req">*</span></div>
                      <CheckedCombo options={qTm?.parameters ?? []} selected={qParams} onChange={setQParams}
                        placeholder="— Select Test first —" disabled={!qTm} searchable />
                    </div>

                    <div className={`qp-row${qTm && qInstrSel.size === 0 ? ' qp-need qp-need-lbl' : ''}`}>
                      <div className="form-label">Instrument <span className="qp-req">*</span></div>
                      <MultiGridLookup<MdlInstrumentOption>
                        rows={qTm?.instruments ?? []} selected={qInstrSel} onChange={setQInstrSel} disabled={!qTm}
                        rowKey={(i) => i.name ?? ''}
                        search={(i) => `${i.number ?? ''} ${i.name ?? ''}`}
                        columns={[{ header: 'InstrumentNumber', cell: (i) => i.number }, { header: 'InstrumentName', cell: (i) => i.name }]}
                        placeholder="— Select Instrument(s) —" disabledPlaceholder="— Select Test first —" minWidth={360} />
                    </div>

                    {/* Read off the retrieved QC samples — the spike amount is a property
                        of the QC that was run, not something typed on this form. */}
                    <div className="qp-row">
                      <div className="form-label">Spike Level</div>
                      <div className={`qp-ro${derivedSpike.level ? '' : ' empty'}`} title="Read from the spike amount on the retrieved QC samples">
                        {derivedSpike.level || '— from loaded QC —'}
                      </div>
                    </div>
                    <div className="qp-row">
                      <div className="form-label">Units</div>
                      <div className={`qp-ro${derivedSpike.units ? '' : ' empty'}`}>{derivedSpike.units || '— from loaded QC —'}</div>
                    </div>

                    <div className="qp-sep" />

                    <div className="qp-row">
                      <div className="form-label">Date Range — From</div>
                      <DateField value={qDateFrom} onChange={setQDateFrom} />
                    </div>
                    <div className="qp-row">
                      <div className="form-label">Date Range — To</div>
                      <DateField value={qDateTo} onChange={setQDateTo} />
                    </div>

                    {qModel === 'verif' && (
                      <div className={`qp-row${qTm && qcTypeSel.size === 0 ? ' qp-need qp-need-lbl' : ''}`}>
                        <div className="form-label">QC Type <span className="qp-req">*</span></div>
                        <MultiGridLookup<MdlQcTypeOption>
                          rows={qcTypesQ.data ?? []} selected={qcTypeSel} onChange={setQcTypeSel} disabled={!qTm}
                          rowKey={(r) => r.qcType ?? ''}
                          columns={[
                            { header: 'QCType', cell: (r) => r.qcType },
                            { header: 'QCRole', cell: (r) => r.qcRole ?? '—' },
                            { header: 'QCRootRole', cell: (r) => r.qcRootRole ?? '—' },
                          ]}
                          search={(r) => `${r.qcType ?? ''} ${r.qcRole ?? ''} ${r.qcRootRole ?? ''}`}
                          placeholder={qcTypesQ.isFetching ? 'Loading QC Types…' : 'Select QC Types'}
                          disabledPlaceholder="— select Test first —" />
                      </div>
                    )}

                    {/* The QC batches actually available in that window — picked, not
                        typed, so the dataset behind the MDL is a visible, auditable
                        selection. Replaces the old QC Category multiselect. */}
                    <div className={`qp-row${qTm && spikePick.size === 0 ? ' qp-need qp-need-lbl' : ''}`}>
                      <div className="form-label">Spike Run Pick <span className="qp-req">*</span></div>
                      <BatchPick kind="S" batches={spikeBatches} picked={spikePick} onChange={setSpikePick}
                        disabled={!qTm} loading={qcBatchesQ.isFetching} min={MDL_MIN} onLoad={doRetrieve} />
                    </div>
                    <div className="qp-row">
                      <div className="form-label"># Spike Samples</div>
                      <div className="qp-autowrap">
                        <div className={`qp-auto${pickedCounts.sPicked > 0 && pickedCounts.sReps >= MDL_MIN ? ' ok' : (pickedCounts.sTotal > 0 && (pickedCounts.sPicked === 0 || pickedCounts.sReps < MDL_MIN) ? ' bad' : '')}`}
                             title="Automatic — the samples the ticked QC batches carry">
                          <b>{pickedCounts.sReps}</b>
                          <span className="qp-of">from {pickedCounts.sPicked} of {pickedCounts.sTotal} run{pickedCounts.sTotal === 1 ? '' : 's'}</span>
                          {pickedCounts.sPicked > 0 && pickedCounts.sReps < MDL_MIN && (
                            <i className="ti ti-alert-triangle qp-warn-ico" title="Below the 40 CFR 136 App. B minimum" />
                          )}
                        </div>
                        {pickedCounts.sPicked > 0 && pickedCounts.sReps < MDL_MIN && (
                          <div className="qp-shortnote"><i className="ti ti-alert-triangle" /><span>
                            Only {pickedCounts.sReps} sample{pickedCounts.sReps === 1 ? '' : 's'} — {MDL_MIN} required.{' '}
                            {pickedCounts.sPicked >= pickedCounts.sTotal ? 'That is every batch in this date range — widen it.' : 'Tick more batches.'}
                          </span></div>
                        )}
                      </div>
                    </div>

                    <div className={`qp-row${qModel !== 'verif' && qTm && blankPick.size === 0 ? ' qp-need qp-need-lbl' : ''}`}>
                      <div className="form-label">Blank Batch Pick {qModel !== 'verif' && <span className="qp-req">*</span>}</div>
                      <BatchPick kind="B" batches={blankBatches} picked={blankPick} onChange={setBlankPick}
                        disabled={!qTm} loading={qcBatchesQ.isFetching} min={MDL_MIN} onLoad={doRetrieve} />
                    </div>
                    <div className="qp-row">
                      <div className="form-label"># Blank Samples</div>
                      <div className="qp-autowrap">
                        <div className={`qp-auto${pickedCounts.bPicked > 0 && (qModel === 'verif' || pickedCounts.bReps >= MDL_MIN) ? ' ok' : (pickedCounts.bTotal > 0 && qModel !== 'verif' && (pickedCounts.bPicked === 0 || pickedCounts.bReps < MDL_MIN) ? ' bad' : '')}`}
                             title="Automatic — the samples the ticked QC batches carry">
                          <b>{pickedCounts.bReps}</b>
                          <span className="qp-of">from {pickedCounts.bPicked} of {pickedCounts.bTotal} run{pickedCounts.bTotal === 1 ? '' : 's'}</span>
                          {qModel !== 'verif' && pickedCounts.bPicked > 0 && pickedCounts.bReps < MDL_MIN && (
                            <i className="ti ti-alert-triangle qp-warn-ico" title="Below the 40 CFR 136 App. B minimum" />
                          )}
                        </div>
                        {qModel !== 'verif' && pickedCounts.bPicked > 0 && pickedCounts.bReps < MDL_MIN && (
                          <div className="qp-shortnote"><i className="ti ti-alert-triangle" /><span>
                            Only {pickedCounts.bReps} sample{pickedCounts.bReps === 1 ? '' : 's'} — {MDL_MIN} required.{' '}
                            {pickedCounts.bPicked >= pickedCounts.bTotal ? 'That is every batch in this date range — widen it.' : 'Tick more batches.'}
                          </span></div>
                        )}
                      </div>
                    </div>

                    <div className="qp-row">
                      <div className="form-label">Total # Replicates</div>
                      <div className="qp-auto" title="Automatic — spike replicates + blank replicates">
                        <b>{pickedCounts.total}</b>
                        {pickedCounts.total > 0 && (
                          <span className="qp-of">{pickedCounts.sReps} spike + {pickedCounts.bReps} blank</span>
                        )}
                      </div>
                    </div>

                    {qModel === 'verif' && (
                      <div className="qp-row">
                        <div className="form-label">Reference MDL</div>
                        <select className="form-control" value={refMdl}
                                onChange={(e) => setRefMdl(e.target.value)} disabled={!qTm}>
                          <option value="">{!qTm ? '— select Test first —' : refMdlsQ.isFetching ? 'Loading…' : '— Select existing MDL —'}</option>
                          {(refMdlsQ.data ?? []).map((m) => (
                            <option key={m.key ?? m.mdlId ?? ''} value={m.key ?? m.mdlId ?? ''}>
                              {[m.mdlId, m.testName, m.methodName, m.finalMdl].filter(Boolean).join(' · ')}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                  </div>
                </div>

                {/* ════ RIGHT · LOADED SAMPLES ════ */}
                <div className="card qp-result">
                  <div className="card-hdr">
                    <div className="card-title">
                      <i className="ti ti-flask" /> Loaded Samples
                      {qLoaded && !retrieveQ.isFetching && qSamples.length > 0 && (
                        <span className="qp-hdr-note"> · {qKept.length} of {qSamples.length} samples retained</span>
                      )}
                    </div>
                    <div className="card-actions">
                      {qRemoved.size > 0 && (
                        <button className="xaf-tb-btn" onClick={() => setQRemoved(new Map())}>
                          <i className="ti ti-arrow-back-up" /> Restore removed
                        </button>
                      )}
                      <div className="compliance-badge"><i className="ti ti-certificate" /> 40 CFR 136 App. B Rev. 2</div>
                    </div>
                  </div>

                  {qLoaded && qTallies.short.length > 0 && !retrieveQ.isFetching && qRows.length > 0 && (
                    <div className="qp-warn">
                      <b><i className="ti ti-alert-triangle" /> Below the 40 CFR 136 App. B §2(b) minimum</b>
                      <ul>{qTallies.short.map((s) => <li key={s}>{s}</li>)}</ul>
                    </div>
                  )}

                  {qLoaded && qSamples.length > 0 && !retrieveQ.isFetching && (
                    <div className="qp-tallies">
                      <div className={`qp-tally${qTallies.nS >= MDL_MIN ? ' ok' : ' bad'}`}>
                        Spiked replicates<b>{qTallies.nS} <span className="qp-of">/ {MDL_MIN}</span></b>
                      </div>
                      <div className={`qp-tally${qModel === 'verif' ? '' : (qTallies.nB >= MDL_MIN ? ' ok' : ' bad')}`}>
                        Method blanks<b>{qTallies.nB} <span className="qp-of">{qModel === 'verif' ? '(ongoing)' : `/ ${MDL_MIN}`}</span></b>
                      </div>
                      <div className={`qp-tally${qTallies.batches >= 3 ? ' ok' : ' bad'}`}>
                        Batches<b>{qTallies.batches} <span className="qp-of">/ 3</span></b>
                      </div>
                      <div className={`qp-tally${qTallies.dates >= 3 ? ' ok' : ' bad'}`}>
                        Dates<b>{qTallies.dates} <span className="qp-of">/ 3</span></b>
                      </div>
                      <div className="qp-tally">Removed<b>{qTallies.removed}</b></div>
                    </div>
                  )}

                  {!qLoaded && (
                    <div className="qp-empty">
                      <i className="ti ti-database-search" />
                      <b>No samples loaded yet</b>
                      <span>Pick a Test, tick the QC batches, then click <b>Load</b>.</span>
                    </div>
                  )}
                  {qLoaded && retrieveQ.isFetching && (
                    <div className="qp-empty"><LoaderSymbol /><b>Retrieving SDMS data…</b></div>
                  )}
                  {qLoaded && retrieveQ.isError && !retrieveQ.isFetching && (
                    <div className="qp-empty">
                      <i className="ti ti-alert-triangle" />
                      <b>Retrieve failed</b>
                      <span><a style={{ color: 'var(--ai-blue)', cursor: 'pointer' }} onClick={() => retrieveQ.refetch()}>Retry</a></span>
                    </div>
                  )}
                  {qLoaded && !retrieveQ.isFetching && !retrieveQ.isError && qRows.length === 0 && (
                    <div className="qp-empty">
                      <i className="ti ti-mood-empty" />
                      <b>No qualifying QC samples</b>
                      <span>Nothing matched this Test, date range and batch selection. Widen the dates or tick more batches.</span>
                    </div>
                  )}
                  {qLoaded && !retrieveQ.isFetching && qSamples.length > 0 && (() => {
                    const pages = Math.max(1, Math.ceil(qSamples.length / qPageSize));
                    const page = Math.min(qPage, pages);
                    const slice = qSamples.slice((page - 1) * qPageSize, page * qPageSize);
                    const units = qSamples.find((s) => s.units)?.units ?? '';
                    const dp = units === 'mg/L' ? 4 : 3;
                    /* One column per analyte. Header is the short form — 'Aluminum (Al)'
                       → 'Al', 'Silica (SiO2)' → 'SiO2' — because 8 full names would not
                       fit; the full name stays as the cell/header tooltip. */
                    const shortName = (a: string) => {
                      const m = /\(([^)]+)\)\s*$/.exec(a);
                      return m ? m[1] : a;
                    };
                    /* A non-detect is REPORTED as '<DL'; showing the 0.0000 numeric
                       instead would misrepresent a blank. Show the reported text when it
                       is a less-than, the numeric otherwise. */
                    const cellText = (c: QCell | undefined) => {
                      if (!c) return '—';
                      if (c.t && c.t.trim().startsWith('<')) return c.t.trim();
                      return c.v == null ? '—' : c.v.toFixed(dp);
                    };
                    return (
                      <>
                        <div className="qp-tbl-wrap">
                          <table className="xaf-table qp-analyte-tbl">
                            <thead>
                              <tr>
                                <th>QC Batch</th>
                                <th title="Samplelogin.SysSampleCode — the QC sample as logged in">SysSampleCode</th>
                                <th>Role</th><th>Type</th>
                                <th>Analyzed</th><th>Instrument</th>
                                <th style={{ textAlign: 'right' }}>Analytes</th>
                                {qAnalytes.map((a) => (
                                  <th key={a} className="qp-an-col" title={units ? `${a} (${units})` : a}>{shortName(a)}</th>
                                ))}
                                <th style={{ width: 70 }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {slice.map((s) => {
                                const gone = qRemoved.has(s.key);
                                return (
                                  <tr key={s.key} className={gone ? 'qp-removed' : ''}>
                                    <td style={{ fontFamily: 'var(--ai-font-mono)' }}>{s.batch}</td>
                                    <td style={{ fontFamily: 'var(--ai-font-mono)' }}>{s.sysCode}</td>
                                    <td><span className={`qp-kind ${s.kind === 'S' ? 's' : 'b'}`}>{s.kind === 'S' ? 'SPIKE' : 'BLANK'}</span></td>
                                    <td>{s.type}</td>
                                    <td>{s.date}</td>
                                    <td>{s.instr}</td>
                                    <td style={{ textAlign: 'right' }}>{s.analytes}</td>
                                    {qAnalytes.map((a) => {
                                      const c = s.cells.get(a);
                                      const nd = !!(c?.t && c.t.trim().startsWith('<'));
                                      return (
                                        <td key={a} className={'qp-an-col' + (nd ? ' qp-an-nd' : '')}
                                            title={c ? `${a}${units ? ' · ' + units : ''}${c.t ? ' · reported ' + c.t : ''}` : a}>
                                          {cellText(c)}
                                        </td>
                                      );
                                    })}
                                    <td className="qp-actcell" style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                      {gone && <span className="qp-rsn" title={qRemoved.get(s.key)}>{qRemoved.get(s.key)}</span>}{' '}
                                      <button className="qp-rm" title={gone ? 'Restore this sample' : 'Remove from the study'}
                                              onClick={() => void toggleQRemoved(s)}>
                                        <i className={gone ? 'ti ti-arrow-back-up' : 'ti ti-x'} />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {units && (
                          <div className="qp-an-note">
                            All analyte results in <b>{units}</b>. A <b>&lt;</b> value is the reported
                            detection limit (non-detect); hover any cell for the full analyte name.
                          </div>
                        )}
                        {/* Shared grid footer — identical to Sample Registration's (§18.6). */}
                        <GridPager total={qSamples.length} page={page} pageSize={qPageSize}
                                   onPageChange={setQPage} onPageSizeChange={setQPageSize} />
                      </>
                    );
                  })()}

                  <div className="alert-info" style={{ margin: 12 }}>
                    <strong>Requirement:</strong> Minimum 7 spiked samples and 7 method blanks, prepared in at least 3 batches on 3 separate calendar dates (40 CFR 136 App. B §2b).
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ════════ RETRIEVE ════════ */}
        {view === 'retrieve' && (
          <div className="page">
            <div className="dp-topbar">
              <div className="dp-breadcrumb">
                <a onClick={() => setView('list')}>MDL Studies</a>
                <i className="ti ti-chevron-right" />
                <a onClick={() => setView('query')}>New {qModel === 'verif' ? 'MDL Verification' : 'Initial MDL'}</a>
                <i className="ti ti-chevron-right" />
                <span>Retrieved Data</span>
              </div>
              <div className="dp-title-row">
                <div className="dp-title">{retrieveTitle || 'Retrieved QC Data'}</div>
                <div className="dp-title-actions">
                  {mdlBadges}
                  <span className="audit-chip" onClick={() => setProcOpen(true)}><i className="ti ti-certificate" /> Procedure Rules</span>
                </div>
              </div>
              <div className="dp-sub">Review the SDMS raw data sheets and confirm which samples enter the calculation.</div>
            </div>
            <div className="dp-scroll">
              <div className="wf-actions">
                <button className="xaf-tb-btn" onClick={() => retrieveQ.refetch()} title={`Refresh (${formatCombo('alt+r')})`}><i className="ti ti-refresh" /> Refresh</button>
                {editStudy
                  ? <button className="xaf-tb-btn" onClick={() => setView('list')}><i className="ti ti-arrow-left" /> Back to List</button>
                  : <button className="xaf-tb-btn" onClick={() => setView('query')}><i className="ti ti-arrow-left" /> Back to Query</button>}
                <button className="xaf-tb-btn primary" onClick={doCalculate}><i className="ti ti-calculator" /> Calculate &amp; Save</button>
              </div>
              <div className="hint"><b>Step 2 · Review Retrieved Data.</b> Confirm the retrieved spiked samples and method blanks below, then click <b>Calculate &amp; Save</b>.</div>
              {retrieveQ.isFetching && (
                <div className="dp-sub" style={{ padding: 16 }}><i className="ti ti-loader" /> Retrieving SDMS data…</div>
              )}
              {retrieveQ.isError && !retrieveQ.isFetching && (
                <div className="alert-warning">Failed to retrieve SDMS data. <a style={{ color: 'var(--ai-blue)', cursor: 'pointer' }} onClick={() => retrieveQ.refetch()}>Retry</a></div>
              )}
              {!retrieveQ.isFetching && !retrieveQ.isError && retrieveStudy && (
                <>
                  <div className="alert-success" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <i className="ti ti-circle-check" style={{ fontSize: 15 }} />
                    <span><strong>{retrieveStudy.nSpike} spiked (LFB) {retrieveStudy.nSpike === 1 ? 'batch' : 'batches'}</strong> and <strong>{retrieveStudy.nBlank} method-blank (LRB) {retrieveStudy.nBlank === 1 ? 'batch' : 'batches'}</strong> across <strong>{(retrieveStudy.analytes || []).length} analytes</strong> retrieved from SDMS. {(retrieveStudy.nSpike ?? 0) >= 7 ? 'Meets the §136 minimum of 7 spiked replicates.' : 'Below the §136 minimum of 7 spiked replicates — for verification only.'}</span>
                  </div>
                  <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(4,1fr)' }} dangerouslySetInnerHTML={H(retrieveKpiHtml)} />
                  {retrieveQ.data?.extended && (
                    <div className="alert-warning" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <i className="ti ti-history" style={{ fontSize: 14 }} />
                      <span>Pooled earlier data (§4): the rolling window was extended back to <strong>{usDate(retrieveQ.data.from)}</strong> {retrieveQ.data.reached ? 'to reach the 7-replicate minimum.' : '— still below 7 spiked replicates over the lookback.'}</span>
                    </div>
                  )}
                  {retrieveStudy._compliance && <CompliancePanel c={retrieveStudy._compliance} modelType={qModel} />}
                  <div className="tab-bar">
                    <div className={'tab' + (retrieveTab === 'raw' ? ' active' : '')} onClick={() => setRetrieveTab('raw')}>Raw Results</div>
                    <div className={'tab' + (retrieveTab === 'mdl' ? ' active' : '')} onClick={() => setRetrieveTab('mdl')}>MDL Study</div>
                  </div>
                  {retrieveTab === 'raw' ? (
                    <div className="card">
                      <div className="card-hdr"><div className="card-title"><i className="ti ti-list-details" /> Raw Results — {retrieveTitle}</div>
                        <div className="card-actions"><span style={{ fontSize: 11, color: 'var(--ai-muted)' }}>SpreadSheetEntry_SampleInfo</span></div>
                      </div>
                      <div style={{ padding: 12 }}><RawResults spike={retrieveStudy._rawSpike ?? []} blank={retrieveStudy._rawBlank ?? []} /></div>
                    </div>
                  ) : (
                    <div className="card">
                      <div className="card-hdr"><div className="card-title"><i className="ti ti-table" /> MDL Study — {retrieveTitle}</div>
                        <div className="card-actions"><span style={{ fontSize: 11, color: 'var(--ai-muted)' }}>LFB = Spiked · LRB = Method Blank</span></div>
                      </div>
                      <div style={{ padding: 12 }} dangerouslySetInnerHTML={H(retrieveMatrixHtml)} />
                      <div style={{ padding: '0 12px 12px' }}>
                        <OutlierControl spikeRows={retrieveStudy._spikeRows ?? []} blankRows={retrieveStudy._blankRows ?? []} excluded={excluded} onChange={setExcluded} />
                      </div>
                    </div>
                  )}
                  <PreAttach files={preAttach} onChange={setPreAttach} />
                </>
              )}
              {!retrieveQ.isFetching && !retrieveQ.isError && !retrieveStudy && (
                <div className="alert-warning">No qualifying LFB/LRB QC samples found for this Test Method, date range, and QC categories. Adjust the query (Step 1) and retrieve again.</div>
              )}
            </div>
          </div>
        )}

        {/* ════════ STUDY DETAIL ════════ */}
        {view === 'study' && !!studyKey && !study && (
          <div className="page">
            <div className="xaf-toolbar">
              <button className="xaf-tb-btn" onClick={() => setView('list')}><i className="ti ti-arrow-left" /> Back to List</button>
            </div>
            <div className="dp-scroll">
              {detailQ.isError
                ? <div className="alert-warning">Failed to load this study. <a style={{ color: 'var(--ai-blue)', cursor: 'pointer' }} onClick={() => detailQ.refetch()}>Retry</a></div>
                : <div className="dp-sub" style={{ padding: 16 }}>Loading study…</div>}
            </div>
          </div>
        )}

        {view === 'study' && study && studyHtml && (
          <div className="page">
            {reportChooser && (
              <div onClick={() => setReportChooser(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 22, width: 560, maxWidth: '92vw', boxShadow: '0 10px 40px rgba(0,0,0,.25)' }}>
                  <div style={{ fontSize: 16, fontWeight: 'bold', color: 'var(--ai-ink)', marginBottom: 4 }}>Choose report layout</div>
                  <div style={{ fontSize: 12, color: 'var(--ai-muted)', marginBottom: 16 }}>Both report types open in a new tab ready to print or save as PDF.</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {([
                      { layout: 'standard' as const, icon: 'ti-file-description', title: 'Standard Report', desc: 'The current report — per-analyte MDL summary, calculation, signatures.' },
                      { layout: 'grid' as const, icon: 'ti-table', title: 'Grid Layout Report', desc: 'The full MDL Study matrix exactly as shown in the grid — Spiked Samples, Method Blanks, and Statistics & MDL Summary (landscape).' },
                    ]).map((o) => (
                      <button key={o.layout} onClick={() => generatePdf(o.layout)}
                        style={{ textAlign: 'left', border: '1px solid var(--ai-line)', borderRadius: 6, padding: '14px 14px', background: 'var(--ai-header-bg)', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 'bold', color: 'var(--ai-blue)', marginBottom: 6 }}><i className={'ti ' + o.icon} style={{ fontSize: 18 }} /> {o.title}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--ai-muted)', lineHeight: 1.4 }}>{o.desc}</div>
                      </button>
                    ))}
                  </div>
                  <div style={{ textAlign: 'right', marginTop: 16 }}>
                    <button className="xaf-tb-btn" onClick={() => setReportChooser(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
            <div className="dp-topbar">
              <div className="dp-breadcrumb">
                <a onClick={() => setView('list')}>MDL Studies</a>
                <i className="ti ti-chevron-right" />
                <span>{study.mdlId}</span>
              </div>
              <div className="dp-title-row">
                <div className="dp-title">{study.title}</div>
                <div className="dp-title-actions">
                  {mdlBadges}
                  <span className="audit-chip" onClick={() => setProcOpen(true)}><i className="ti ti-certificate" /> Procedure Rules</span>
                </div>
              </div>
              <div className="dp-sub" dangerouslySetInnerHTML={H(study.sub)} />
            </div>
            <div className="dp-scroll">
              <div className="wf-actions">
                <button className="xaf-tb-btn" onClick={() => setReportChooser(true)} title={`PDF Report (${formatCombo('alt+g')})`}><i className="ti ti-file-type-pdf" /> PDF Report</button>
                {/* Pending Submission: this review page is the Submit step. "Edit" reopens the editable
                    Calculate & Save matrix to revise before submitting; Submit captures the e-signature. */}
                {studyStatus === 'pending' && <button className="xaf-tb-btn" onClick={doEditStudy} title={`Edit (Calculate & Save) (${formatCombo('alt+e')})`}><i className="ti ti-edit" /> Edit (Calculate &amp; Save)</button>}
                {/* C5 — roll a Submitted/Validated/Approved study back to Pending Submission for revision. */}
                {(studyStatus === 'draft' || studyStatus === 'validated' || studyStatus === 'approved') && <button className="xaf-tb-btn" onClick={doRollback} title={`Roll Back (${formatCombo('alt+b')})`}><i className="ti ti-arrow-back-up" /> Roll Back</button>}
                <button className="xaf-tb-btn" onClick={() => setView('list')} title={`Back to List (${formatCombo('esc')})`}><i className="ti ti-arrow-left" /> Back to List</button>
                {wf.showSubmit && <button className="xaf-tb-btn primary" onClick={doSubmit} title={`Submit (${formatCombo('alt+u')})`}><i className="ti ti-send" /> Submit</button>}
                {wf.showValidate && <button className="xaf-tb-btn primary" onClick={doValidate} title={`Validate (${formatCombo('alt+v')})`}><i className="ti ti-shield" /> Validate</button>}
                {wf.showApprove && <button className="xaf-tb-btn primary" onClick={doApprove} title={`Approve (${formatCombo('alt+a')})`}><i className="ti ti-shield-check" /> Approve</button>}
                {wf.showRelease && <button className="xaf-tb-btn primary" onClick={doRelease} title={`Release (${formatCombo('alt+y')})`}><i className="ti ti-rocket" /> Release</button>}
                {studyStatus === 'released' && <button className="xaf-tb-btn primary" onClick={applyMdlNow} title={`Apply MDL (${formatCombo('alt+shift+a')})`}><i className="ti ti-database-import" /> Apply MDL</button>}
              </div>
              <div className={'hint' + (studyStatus === 'released' ? ' ok' : '')}>
                {studyStatus === 'pending' ? <><b>Step 5 · Submit.</b> Review the calculation and results below, then click <b>Submit</b> to e-sign and submit the study.</>
                 : studyStatus === 'draft' ? (wf.showValidate ? <><b>Validate.</b> Review the submitted results below, then click <b>Validate</b>.</>
                     : wf.showApprove ? <><b>Approve.</b> The Validate level is disabled — review the submitted results below, then click <b>Approve</b>.</>
                     : <><b>Release.</b> The Validate &amp; Approve levels are disabled — review the submitted results below, then click <b>Release</b>.</>)
                 : studyStatus === 'validated' ? (wf.showApprove ? <><b>Approve.</b> Review the validated results below, then click <b>Approve</b>.</>
                     : <><b>Release.</b> The Approve level is disabled — review the validated results below, then click <b>Release</b>.</>)
                 : studyStatus === 'approved' ? <><b>Step 8 · Release.</b> Review the approved study below, then click <b>Release</b>.</>
                 : <><b>Released.</b> This MDL is released and effective — click <b>Apply MDL</b> to make it the active method detection limit.</>}
              </div>

              <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(4,1fr)' }} dangerouslySetInnerHTML={H(studyHtml.kpis)} />

              <div className="tab-bar">
                <div className={'tab' + (tab === 'spikes' ? ' active' : '')} onClick={() => setTab('spikes')}>Spiked Samples ({((study.multiAnalyte ? study._spikeRows : study.spikeRows) || []).length})</div>
                <div className={'tab' + (tab === 'blanks' ? ' active' : '')} onClick={() => setTab('blanks')}>Method Blanks ({((study.multiAnalyte ? study._blankRows : study.blankRows) || []).length})</div>
                <div className={'tab' + (tab === 'calc' ? ' active' : '')} onClick={() => setTab('calc')}>Calculation</div>
                <div className={'tab' + (tab === 'docs' ? ' active' : '')} onClick={() => setTab('docs')}>Documentation</div>
              </div>

              {tab === 'spikes' && (study.multiAnalyte
                ? <div className="card"><div style={{ padding: 12 }} dangerouslySetInnerHTML={H(studyHtml.spikes)} /></div>
                : (
                  <div className="card">
                    <div className="card-hdr"><div className="card-title"><i className="ti ti-flask" /> Spiked Sample Results</div></div>
                    <div className="table-wrap" dangerouslySetInnerHTML={H(studyHtml.spikes)} />
                    <div style={{ padding: '6px 12px', background: 'var(--ai-header-bg)', borderTop: '1px solid var(--ai-line-soft)', fontSize: 11, color: 'var(--ai-muted)', display: 'flex', gap: 20, flexWrap: 'wrap' }} dangerouslySetInnerHTML={H(study.spikeFooter || '')} />
                  </div>
                ))}

              {tab === 'blanks' && (study.multiAnalyte
                ? <div className="card"><div style={{ padding: 12 }} dangerouslySetInnerHTML={H(studyHtml.blanks)} /></div>
                : (
                  <div className="card">
                    <div className="card-hdr"><div className="card-title"><i className="ti ti-test-pipe" /> Method Blank Results</div></div>
                    <div className="table-wrap" dangerouslySetInnerHTML={H(studyHtml.blanks)} />
                    <div style={{ padding: '6px 12px', background: 'var(--ai-header-bg)', borderTop: '1px solid var(--ai-line-soft)', fontSize: 11, color: 'var(--ai-muted)', display: 'flex', gap: 20, flexWrap: 'wrap' }} dangerouslySetInnerHTML={H(study.blankFooter || '')} />
                  </div>
                ))}

              {tab === 'calc' && (
                <div className="card">
                  <div className="card-hdr"><div className="card-title"><i className="ti ti-calculator" /> Calculation Summary</div></div>
                  <div dangerouslySetInnerHTML={H(studyHtml.calc)} />
                </div>
              )}

              {tab === 'docs' && (
                <div className="card">
                  <div className="card-hdr">
                    <div className="card-title"><i className="ti ti-clipboard-list" /> Documentation Record</div>
                    <div className="card-actions">
                      <button className="ca-btn" onClick={() => fileRef.current?.click()}><i className="ti ti-paperclip" /> Attach</button>
                    </div>
                  </div>
                  <div dangerouslySetInnerHTML={H(studyHtml.docs)} />
                  {(studyStatus === 'validated' || studyStatus === 'approved' || studyStatus === 'released') && (
                    // token-exempt: #534AB7 is the Validated workflow-stage category color (data-viz) — no purple token exists
                    <div className="calc-row"><span className="calc-lbl">Validated</span><span className="calc-val" style={{ color: '#534AB7', fontWeight: 'bold' }}>{docCompleted('Validate', 'Completed · S. Park')}</span></div>
                  )}
                  {(studyStatus === 'approved' || studyStatus === 'released') && (
                    <div className="calc-row"><span className="calc-lbl">Approved</span><span className="calc-val" style={{ color: 'var(--ai-blue)', fontWeight: 'bold' }}>{docCompleted('Approve', 'Completed · Dr. N. Rao')}</span></div>
                  )}
                  {studyStatus === 'released' && (
                    <div className="calc-row"><span className="calc-lbl">Released</span><span className="calc-val" style={{ color: 'var(--ai-green)', fontWeight: 'bold' }}>{docCompleted('Release', 'Completed · System')}</span></div>
                  )}
                </div>
              )}

              {/* Raw data & attachments */}
              <div className="card">
                <div className="card-hdr">
                  <div className="card-title"><i className="ti ti-paperclip" /> Raw Data &amp; Attachments</div>
                  <div className="card-actions"><button className="ca-btn primary" onClick={() => fileRef.current?.click()}><i className="ti ti-upload" /> Attach Files</button></div>
                </div>
                <input type="file" ref={fileRef} multiple style={{ display: 'none' }} onChange={(e) => onAttachFiles(e.target.files)} />
                <div id="attach-dropzone"
                  onDragOver={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement).classList.add('dz-over'); }}
                  onDragLeave={(e) => (e.currentTarget as HTMLElement).classList.remove('dz-over')}
                  onDrop={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement).classList.remove('dz-over'); onAttachFiles(e.dataTransfer.files); }}
                  onClick={() => fileRef.current?.click()}
                  style={{ margin: '10px 12px', padding: 16, border: '2px dashed var(--ai-line)', borderRadius: 6, textAlign: 'center', color: 'var(--ai-muted)', fontSize: 11, cursor: 'pointer' }}>
                  <i className="ti ti-cloud-upload" style={{ fontSize: 22, color: 'var(--ai-muted)' }} /><br />
                  Drag &amp; drop raw data files here, or click to browse<br />
                  <span style={{ fontSize: 10, color: 'var(--ai-muted)' }}>Instrument files (.d, .raw, .csv), PDFs, spreadsheets, logbook scans…</span>
                </div>
                <div onClick={onAttachClick} dangerouslySetInnerHTML={H(studyHtml.attach)} />
              </div>

              {/* Electronic signatures */}
              {study._signatures && study._signatures.length > 0 && (
                <div className="card">
                  <div className="card-hdr">
                    <div className="card-title"><i className="ti ti-writing-sign" /> Electronic Signatures &amp; Audit Trail</div>
                    <div className="card-actions">
                      <button className="ca-btn" onClick={() => setEsigProcOpen(true)}><i className="ti ti-help-circle" /> Signing Procedure</button>
                      <span className="compliance-badge"><i className="ti ti-lock" /> Audit-locked</span>
                    </div>
                  </div>
                  <div dangerouslySetInnerHTML={H(studyHtml.sigs)} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ════════ MODAL: Choose Study Type ════════ */}
      <div className={'modal-overlay' + (typeOpen ? ' open' : '')}>
        <div className="modal">
          <div className="modal-hdr"><div className="modal-title">New MDL Study</div><span className="modal-close" onClick={() => setTypeOpen(false)}><i className="ti ti-x" /></span></div>
          <div className="modal-body">
            <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginBottom: 10 }}>Select the study type to continue. This determines the query fields and data source.</div>
            <div className={'type-card' + (selType === 'initial' ? ' selected' : '')} onClick={() => setSelType('initial')}>
              <div className="type-card-icon ic-o"><i className="ti ti-flask" /></div>
              <div><div className="type-card-title">Initial MDL</div><div className="type-card-desc">New method, or no qualifying data within last 24 months. Pulls raw SDMS data sheets for spiked samples and blanks.</div></div>
            </div>
            <div className={'type-card' + (selType === 'verif' ? ' selected' : '')} onClick={() => setSelType('verif')}>
              <div className="type-card-icon ic-p"><i className="ti ti-refresh" /></div>
              <div><div className="type-card-title">MDL Verification (Ongoing MDL)</div><div className="type-card-desc">Recurring re-verification (quarterly, mid-year, or annual) against an existing released MDL, using recently collected data.</div></div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="ca-btn" onClick={() => setTypeOpen(false)}>Cancel</button>
            <button className="ca-btn primary" onClick={proceedToQuery}><i className="ti ti-arrow-right" /> Continue</button>
          </div>
        </div>
      </div>

      {/* ════════ MODAL: Apply MDL — choose parameters (C12) ════════ */}
      {applyPick && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 460, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-hdr"><div className="modal-title"><i className="ti ti-database-import" /> Apply MDL — choose parameters</div><span className="modal-close" onClick={() => setApplyPick(null)}><i className="ti ti-x" /></span></div>
            <div className="modal-body" style={{ overflowY: 'auto' }}>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--ai-muted)' }}>Select the parameters to write the new Final MDL into the Sample Parameters (TestParameter.MDL).</p>
              <div style={{ display: 'flex', gap: 14, margin: '0 0 8px', fontSize: 11 }}>
                <a style={{ color: 'var(--ai-blue)', cursor: 'pointer' }} onClick={() => setApplyPick((p) => p && ({ ...p, sel: new Set(p.names) }))}>Select all</a>
                <a style={{ color: 'var(--ai-blue)', cursor: 'pointer' }} onClick={() => setApplyPick((p) => p && ({ ...p, sel: new Set<string>() }))}>Clear</a>
              </div>
              {applyPick.names.map((nm) => (
                <label key={nm} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12 }}>
                  <input type="checkbox" checked={applyPick.sel.has(nm)} onChange={() => setApplyPick((p) => { if (!p) return p; const s = new Set(p.sel); if (s.has(nm)) s.delete(nm); else s.add(nm); return { ...p, sel: s }; })} />
                  {nm}
                </label>
              ))}
            </div>
            <div className="modal-footer">
              <button className="ca-btn" onClick={() => setApplyPick(null)}>Cancel</button>
              <button className="ca-btn primary" disabled={applyPick.sel.size === 0} onClick={() => { const names = [...applyPick.sel]; setApplyPick(null); void runApplyMdl(names); }}><i className="ti ti-database-import" /> Apply to {applyPick.sel.size}</button>
            </div>
          </div>
        </div>
      )}

      {/* ════════ MODAL: EPA MDL Procedure Reference ════════ */}
      <div className={'modal-overlay' + (procOpen ? ' open' : '')}>
        <div className="modal" style={{ width: 680, maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
          <div className="modal-hdr"><div className="modal-title"><i className="ti ti-certificate" /> EPA MDL Procedure — Key Rules (40 CFR 136 App. B, Rev 2)</div><span className="modal-close" onClick={() => setProcOpen(false)}><i className="ti ti-x" /></span></div>
          <div className="modal-body" style={{ overflowY: 'auto' }} dangerouslySetInnerHTML={H(PROC_HTML)} />
          <div className="modal-footer"><button className="ca-btn primary" onClick={() => setProcOpen(false)}>Close</button></div>
        </div>
      </div>

      {/* ════════ MODAL: Electronic Signature Procedure (SOP) ════════ */}
      <div className={'modal-overlay' + (esigProcOpen ? ' open' : '')}>
        <div className="modal" style={{ width: 700, maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
          <div className="modal-hdr"><div className="modal-title"><i className="ti ti-writing-sign" /> Electronic Signature — Procedure (SOP)</div><span className="modal-close" onClick={() => setEsigProcOpen(false)}><i className="ti ti-x" /></span></div>
          <div className="modal-body" style={{ overflowY: 'auto' }} dangerouslySetInnerHTML={H(ESIG_PROC_HTML)} />
          <div className="modal-footer"><button className="ca-btn primary" onClick={() => setEsigProcOpen(false)}>Close</button></div>
        </div>
      </div>

      {/* ════════ MODAL: Electronic Signature ════════ */}
      <div className={'modal-overlay' + (esign ? ' open' : '')}>
        <div className="modal" style={{ width: 500 }}>
          <div className="modal-hdr"><div className="modal-title"><i className="ti ti-writing-sign" /> Electronic Signature — {part11 ? '21 CFR Part 11' : 'ISO/IEC 17025'}</div><span className="modal-close" onClick={cancelSignature}><i className="ti ti-x" /></span></div>
          <div className="modal-body">
            <div className="alert-warning" style={{ marginBottom: 12 }} dangerouslySetInnerHTML={H(part11
              ? (ESIGN_REQUIRE_CREDENTIALS
                  ? '<strong>21 CFR Part 11 mode.</strong> Two-component authentication (User ID + password) per §11.200. This signature is the legally binding equivalent of your handwritten signature and is permanently linked to this record.'
                  : '<strong>21 CFR Part 11 mode.</strong> This signature is the legally binding equivalent of your handwritten signature and is permanently linked to this record. (Password + MFA re-entry are temporarily disabled.)')
              : '<strong>ISO/IEC 17025 signing.</strong> You are already authenticated; your name, date/time and meaning are captured to the audit trail (§7.5, §8.4). No password re-entry is required in this mode.')} />
            <div style={{ fontSize: 12, color: 'var(--ai-muted)', marginBottom: 12, padding: '8px 11px', background: 'var(--ai-readonly-bg)', border: '1px solid var(--ai-line)', borderRadius: 3, lineHeight: 1.7 }}>
              Record: <strong>{meaningRecord}</strong><br />
              Action: <strong>{esign?.action}</strong><br />
              Signed as: <strong>{currentUser.name} · {currentUser.role}</strong><br />
              Authority required: <strong>{esign ? (ACTION_REQ[esign.action] || 'Any') : '—'}</strong>
            </div>
            <div style={{ textAlign: 'right', margin: '-6px 0 8px' }}><a onClick={() => setEsigProcOpen(true)} style={{ fontSize: 11, color: 'var(--ai-blue)', cursor: 'pointer' }}><i className="ti ti-help-circle" style={{ verticalAlign: -2 }} /> How electronic signing works</a></div>
            <div className="form-grid-2" style={{ marginBottom: 10 }}>
              <div className="form-group"><div className="form-label">User ID</div><input className="form-control" value={currentUser.id} readOnly style={{ background: 'var(--ai-readonly-bg)' }} /></div>
              {part11 && ESIGN_REQUIRE_CREDENTIALS && (
                <div className="form-group"><div className="form-label">Password <span style={{ color: 'var(--ai-orange)' }}>*</span></div><input className="form-control" type="password" placeholder="Enter your password" autoComplete="off" value={ePass} onChange={(e) => setEPass(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') confirmSignature(); }} /></div>
              )}
            </div>
            {part11 && ESIGN_REQUIRE_CREDENTIALS && (
              <div className="form-group" style={{ marginBottom: 10 }}>
                <div className="form-label">Authenticator (MFA) Code <span style={{ color: 'var(--ai-orange)' }}>*</span></div>
                <input className="form-control" inputMode="numeric" maxLength={6} placeholder="6-digit code from your authenticator app" autoComplete="off" value={eMfa} onChange={(e) => setEMfa(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') confirmSignature(); }} />
                <div style={{ fontSize: 10, color: 'var(--ai-muted)', marginTop: 3 }}><i className="ti ti-info-circle" style={{ verticalAlign: -2 }} /> Prototype: any 6 digits work (e.g. <strong>123456</strong>) — no real authenticator is wired.</div>
              </div>
            )}
            <div className="form-group" style={{ marginBottom: 10 }}><div className="form-label">Meaning of Signature <span style={{ color: 'var(--ai-orange)' }}>*</span></div>
              <select className="form-control" value={eMeaning} onChange={(e) => setEMeaning(e.target.value)}>{(esign?.meanings || []).map((m) => <option key={m}>{m}</option>)}</select>
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}><div className="form-label">Reason / Comment</div><input className="form-control" placeholder="Optional" value={eReason} onChange={(e) => setEReason(e.target.value)} /></div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 11, color: 'var(--ai-muted)', cursor: 'pointer', lineHeight: 1.5 }}>
              <input type="checkbox" checked={eAttest} onChange={(e) => setEAttest(e.target.checked)} style={{ marginTop: 2, flexShrink: 0, accentColor: 'var(--ai-blue)' }} />
              <span>{attestText}</span>
            </label>
            {eError && <div style={{ color: 'var(--ai-red)', fontSize: 11, marginTop: 9 }}><i className="ti ti-alert-triangle" style={{ verticalAlign: -2 }} /> {eError}</div>}
          </div>
          <div className="modal-footer">
            <button className="ca-btn" onClick={cancelSignature}>Cancel</button>
            <button className="ca-btn primary" onClick={confirmSignature}><i className="ti ti-writing-sign" /> Sign &amp; Continue</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════ static modal bodies (verbatim from the prototype) ════════
   token-exempt (Standard 1): PROC_HTML / ESIG_PROC_HTML are verbatim prototype
   HTML-string modal bodies injected via dangerouslySetInnerHTML — document/report
   markup, the same report-colour exemption as mdlCalc.ts. Kept exact so the
   procedure / e-sign help content renders identically to the source. */
const PROC_HTML = `
<div style="margin-bottom:14px;padding:10px 12px;background:#f4f8fc;border:1px solid #d6e4f0;border-radius:5px;font-size:12px;color:#23405a;line-height:1.5">
  <b>Method Detection Limit (MDL).</b> The <b>minimum measured concentration</b> of a substance that can be reported with <b>99% confidence</b> that the measured concentration is <b>distinguishable from method-blank results</b>.
</div>
<div style="margin-bottom:16px;border:1px solid #ddd;border-radius:5px;overflow:hidden">
  <div style="background:#fff3ec;padding:8px 12px;font-weight:bold;color:#c45a08;font-size:12px;display:flex;align-items:center;gap:6px;border-bottom:1px solid #f3d6c2">
    <i class="ti ti-flask"></i> Initial MDL &nbsp;<span style="font-weight:normal;color:#999;font-size:11px">— §2, new method / instrument</span>
  </div>
  <div style="padding:6px 0">
    <div class="proc-rule"><b>Two-part determination.</b> The MDL is computed from <b>both</b> spiked samples (MDL<sub>s</sub>) and method blanks (MDL<sub>b</sub>); the reported MDL is the <b>greater</b> of the two.</div>
    <div class="proc-rule"><b>Replicates.</b> Process a minimum of <b>7 spiked replicates</b> and <b>7 method blanks</b> through all steps of the method, on each instrument.</div>
    <div class="proc-rule"><b>Spread over time.</b> Spikes and blanks must span <b>≥ 3 separate batches on ≥ 3 separate calendar dates</b> — not all in one batch/day.</div>
    <div class="proc-rule"><b>Spiking level.</b> Spike at <b>typically 2–10× the estimated MDL</b> — low enough to be near the limit, high enough to be reliably detected; <b>this is not a must — some analytes fall outside this range</b>. The range is <b>an estimate, used as guidance only</b>: a spike outside it is shown for information and <b>never flagged as an error or blocked from saving</b>.</div>
    <div class="proc-rule"><b>Spiked MDL.</b> MDL<sub>s</sub> = <b>t<sub>(n−1, 0.99)</sub> × S<sub>s</sub></b>, where S<sub>s</sub> = std dev of the spiked results and t = one-sided 99% Student's t for n−1 df (e.g. <b>3.143</b> for n = 7).</div>
    <div class="proc-rule"><b>Blank MDL.</b>
      <div style="margin-top:3px;padding-left:10px;color:#444">
        • No blanks give a numerical result → MDL<sub>b</sub> not used; <b>MDL = MDL<sub>s</sub></b>.<br>
        • <b>Some</b> (not all) blanks numerical → MDL<sub>b</sub> = <b>highest method blank</b> result.<br>
        • <b>All</b> blanks numerical → MDL<sub>b</sub> = <b>X̄<sub>blank</sub> + t<sub>(n−1, 0.99)</sub> × S<sub>blank</sub></b>.<br>
        • <b>More than 100</b> method-blank results available → MDL<sub>b</sub> = the <b>99th percentile</b> of the blank results (Rev. 2 large-dataset rule — trims a single high outlier).
      </div>
    </div>
    <div class="proc-rule"><b>Non-detects.</b> A non-detect blank is <b>not set to zero</b> — forcing it to zero would artificially compress the data and bias the standard deviation low, giving an unrealistically small MDL. Non-detects are <b>excluded from the blank mean and standard deviation</b> (they still count toward the blank total that selects the case above).</div>
    <div class="proc-rule"><b>Final MDL = max(MDL<sub>s</sub>, MDL<sub>b</sub>).</b></div>
    <div class="proc-rule"><b>Outliers.</b> Exclude a result only with documented statistical/technical justification, and replace it to keep n ≥ 7.</div>
  </div>
</div>
<div style="border:1px solid #ddd;border-radius:5px;overflow:hidden">
  <div style="background:#EEEDFE;padding:8px 12px;font-weight:bold;color:#534AB7;font-size:12px;display:flex;align-items:center;gap:6px;border-bottom:1px solid #ddd9f7">
    <i class="ti ti-refresh"></i> MDL Verification &nbsp;<span style="font-weight:normal;color:#999;font-size:11px">— §3–4, ongoing / annual</span>
  </div>
  <div style="padding:6px 0">
    <div class="proc-rule"><b>Verification frequency.</b> Re-verify each MDL on an ongoing basis (quarterly, mid-year, or annual) — and <b>at least once every 13 months</b>, on each instrument.</div>
    <div class="proc-rule"><b>Ongoing spikes.</b> Collect data continuously — include an MDL spike <b>at least quarterly (≥ 2 per quarter)</b> plus the routine method blanks. <b>Quarters in which the method was not run are exempt</b> — the cadence applies only to quarters with sample activity.</div>
    <div class="proc-rule"><b>Recalculate.</b> At least annually, recompute MDL<sub>s</sub> and MDL<sub>b</sub> from the spikes &amp; blanks of the <b>past 12 months</b>, using the same equations as the initial determination (pool with earlier data to reach the minimum n if needed).</div>
    <div class="proc-rule"><b>99th-percentile blank MDL (large datasets).</b> Ongoing verification accumulates method blanks continuously, so the recalculation window often holds many blank results. When <b>more than 100</b> method-blank results are available, MDL<sub>b</sub> = the <b>99th percentile</b> of the blank results (Rev. 2 large-dataset rule) instead of X̄<sub>blank</sub> + t<sub>(n−1, 0.99)</sub> × S<sub>blank</sub> — this trims a single high outlier and is the expected path for established methods with continuous blank data.</div>
    <div class="proc-rule"><b>Retain-or-update decision.</b>
      <div style="margin-top:3px;padding-left:10px;color:#444">
        • Recalculated MDL is <b>within 0.5×–2.0×</b> of the current MDL <b>AND</b> &lt; 3% of method-blank results over the past 2 years exceed the current MDL → the <b>current MDL may be retained</b>.<br>
        • Otherwise → <b>replace</b> with the newly calculated MDL (or take corrective action / re-determine).
      </div>
    </div>
    <div class="proc-rule"><b>New instrument or method change</b> → run a new <b>Initial MDL</b>.</div>
  </div>
</div>
<div style="font-size:10px;color:#999;margin-top:12px;line-height:1.5">
  Plain-language summary of <b>40 CFR Part 136, Appendix B, Revision 2</b> (EPA, 2017 Methods Update Rule). The published regulation text is controlling; use this panel as a working reference, not a substitute.
</div>`;

const ESIG_PROC_HTML = `
<div style="border:1px solid #ddd;border-radius:5px;overflow:hidden;margin-bottom:14px">
  <div style="background:#E6F1FB;padding:8px 12px;font-weight:bold;color:#185FA5;font-size:12px"><i class="ti ti-shield-lock"></i> Two signing standards</div>
  <div style="padding:6px 0">
    <div class="proc-rule"><b>ISO/IEC 17025 (default, always on).</b> Lightweight signing — your <b>name, date/time and meaning</b> are captured to the audit trail with an <b>authority check</b>. Single-component (you are already authenticated). Expected for environmental / CWA work under 40 CFR 136.</div>
    <div class="proc-rule"><b>21 CFR Part 11 (toggle-on layer).</b> Adds a <b>second component</b> (password) plus an <b>MFA authenticator code</b>; the signature is the legally binding equivalent of a handwritten signature. Switch on only for clients/records under an FDA predicate rule. Toggle in the top bar (<i>Signing: …</i>).</div>
  </div>
</div>
<div style="border:1px solid #ddd;border-radius:5px;overflow:hidden;margin-bottom:14px">
  <div style="background:#EEEDFE;padding:8px 12px;font-weight:bold;color:#534AB7;font-size:12px"><i class="ti ti-users"></i> Authority (who can sign what)</div>
  <div style="padding:6px 0">
    <div class="proc-rule"><b>Submit</b> — the analyst who authored the study.</div>
    <div class="proc-rule"><b>Validate / Approve / Release</b> — the reviewer, who for an MDL study is the <b>Lab Supervisor</b>.</div>
    <div class="proc-rule" style="color:#888"><b>Separation of duties is not enforced</b> for MDL studies — the submitter is not blocked from the later steps; the Lab Supervisor performs the review. A study that has <b>not yet been reviewed</b> can be <b>rolled back by the author</b> to revise and re-submit.</div>
  </div>
</div>
<div style="border:1px solid #ddd;border-radius:5px;overflow:hidden;margin-bottom:14px">
  <div style="background:#fff3ec;padding:8px 12px;font-weight:bold;color:#c45a08;font-size:12px"><i class="ti ti-list-numbers"></i> How to sign — step by step</div>
  <div style="padding:4px 0">
    <div class="sop-step"><div class="sop-num">1</div><div>Open the study and click the workflow action — <b>Submit</b> (on <i>Calculate &amp; Save</i>), then <b>Validate → Approve → Release</b>.</div></div>
    <div class="sop-step"><div class="sop-num">2</div><div>The signing dialog opens for that action (MDL review is performed by the <b>Lab Supervisor</b>; the submitter is not blocked from the later steps).</div></div>
    <div class="sop-step"><div class="sop-num">3</div><div>The signing dialog shows the <b>record, action, your name &amp; role, and the authority required</b>.</div></div>
    <div class="sop-step"><div class="sop-num">4</div><div>Select the <b>Meaning of signature</b> and add an optional reason / comment.</div></div>
    <div class="sop-step"><div class="sop-num">5</div><div><b>Part 11 mode only:</b> enter your <b>password</b> (2nd component) and your <b>6-digit MFA code</b>.</div></div>
    <div class="sop-step"><div class="sop-num">6</div><div>Read and check the <b>attestation</b> statement, then click <b>Sign &amp; Continue</b>.</div></div>
    <div class="sop-step"><div class="sop-num">7</div><div>The signature is <b>bound to the record</b> — name, User ID, role, date/time, meaning, standard, and a <b>SHA-256 record-hash snapshot</b> — and written to the audit trail.</div></div>
    <div class="sop-step"><div class="sop-num">8</div><div>The workflow advances; the signature appears in the <b>Documentation</b> tab, on the <b>PDF report</b>, and the signature count shows in the <b>list view</b>.</div></div>
  </div>
</div>
<div style="border:1px solid #ddd;border-radius:5px;overflow:hidden">
  <div style="background:#E1F5EE;padding:8px 12px;font-weight:bold;color:#0F6E56;font-size:12px"><i class="ti ti-fingerprint"></i> Integrity &amp; security</div>
  <div style="padding:6px 0">
    <div class="proc-rule"><b>Manifestation (§11.50).</b> Every signature records printed name, date/time, and meaning.</div>
    <div class="proc-rule"><b>Hash snapshot.</b> At signing, a hash of the record state (data + prior signatures) is computed and stored with the signature → <b>tamper-evident</b>; altering the record after signing breaks the hash.</div>
    <div class="proc-rule"><b>MFA.</b> In Part 11 mode a time-based authenticator (TOTP) code is required in addition to the password.</div>
    <div class="proc-rule"><b>Immutability (§11.70).</b> Signatures are permanently bound to the record and cannot be edited or removed.</div>
  </div>
</div>
<div style="font-size:10px;color:#999;margin-top:12px;line-height:1.5">
  Prototype note: in production the password/MFA re-authenticate against the API, the hash is a real <b>SHA-256</b> over the canonical record, and the signature is written via <code>IAuditTrailService</code>. Whether the Part 11 layer (2nd component + MFA) is available is a per-tenant/division feature flag, not a user toggle.
</div>`;

export default MdlStudiesPage;
